import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRelatedArticles } from '@shared/utils/blog';

interface BlogPostData {
  id: number;
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  tag: string;
  content: string;
}

interface RelatedArticlesProps {
  currentPost: BlogPostData;
  allPosts: BlogPostData[];
}

const RelatedArticles: React.FC<RelatedArticlesProps> = ({ currentPost, allPosts }) => {
  const relatedPosts = getRelatedArticles(currentPost, allPosts, 3);

  if (relatedPosts.length === 0) {
    return null;
  }

  return (
    <div className="mt-20 mb-12 bg-gradient-to-r from-slate-50 to-blue-50/30 p-10 rounded-3xl border-2 border-slate-200 shadow-lg relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-blue-100/50 rounded-full blur-2xl -translate-y-6 translate-x-6"></div>
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-indigo-100/50 rounded-full blur-xl translate-y-4 -translate-x-4"></div>

      <div className="relative z-10">
        <h4 className="text-xl font-bold text-slate-800 mb-8 flex items-center gap-3">
          <span className="text-blue-600 text-2xl">📚</span>
          Related Articles
        </h4>
        <div className="grid md:grid-cols-3 gap-6">
        {relatedPosts.map((relatedPost) => (
          <a
            key={relatedPost.id}
            href={`/blog/${relatedPost.slug}`}
            className="group block p-4 bg-white rounded-xl border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium">
                {relatedPost.category}
              </span>
              <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs">
                {relatedPost.tag}
              </span>
            </div>
            <h5 className="font-bold text-slate-800 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
              {relatedPost.title}
            </h5>
            <p className="text-slate-600 text-sm mb-3 line-clamp-2">
              {relatedPost.excerpt}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-blue-600 font-bold text-sm">Read Article →</span>
              <span className="text-slate-400 text-xs">{relatedPost.readTime}</span>
            </div>
          </a>
        ))}
      </div>
      </div>
    </div>
  );
};

const BlogPost: React.FC = () => {
  const navigate = useNavigate();

  // Since we're not using React Router Routes, parse slug from pathname
  const slug = window.location.pathname.split('/blog/')[1];

  console.log('Parsed slug:', slug);
  console.log('Current pathname:', window.location.pathname);

  // SEO optimization for blog posts
  React.useEffect(() => {
    const currentPost = blogPosts.find(p => p.slug === slug);
    if (currentPost) {
      // Update page title
      document.title = `${currentPost.title} | NHS Interview Preparation`;

      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', currentPost.excerpt);
      }

      // Update Open Graph tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogDescription = document.querySelector('meta[property="og:description"]');
      const ogUrl = document.querySelector('meta[property="og:url"]');
      const ogType = document.querySelector('meta[property="og:type"]');
      const ogImage = document.querySelector('meta[property="og:image"]');
      const ogSiteName = document.querySelector('meta[property="og:site_name"]');

      if (ogTitle) ogTitle.setAttribute('content', currentPost.title);
      if (ogDescription) ogDescription.setAttribute('content', currentPost.excerpt);
      if (ogUrl) ogUrl.setAttribute('content', `https://nhsinterview.com/blog/${slug}`);
      if (ogType) ogType.setAttribute('content', 'article');
      if (ogImage) ogImage.setAttribute('content', 'https://nhsinterview.com/og-image.png');
      if (ogSiteName) ogSiteName.setAttribute('content', 'NHS Interview Preparation');

      // Update Twitter Card tags
      const twitterCard = document.querySelector('meta[name="twitter:card"]');
      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      const twitterDescription = document.querySelector('meta[name="twitter:description"]');
      const twitterImage = document.querySelector('meta[name="twitter:image"]');

      if (twitterCard) twitterCard.setAttribute('content', 'summary_large_image');
      if (twitterTitle) twitterTitle.setAttribute('content', currentPost.title);
      if (twitterDescription) twitterDescription.setAttribute('content', currentPost.excerpt);
      if (twitterImage) twitterImage.setAttribute('content', 'https://nhsinterview.com/og-image.png');

      // Add canonical URL
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', `https://nhsinterview.com/blog/${slug}`);

      // Add structured data for blog post
      const existingScript = document.querySelector('script[data-blog-post]');
      if (existingScript) {
        existingScript.remove();
      }

      // Calculate word count for structured data
      const wordCount = currentPost.content.replace(/<[^>]*>/g, '').split(/\s+/).length;

      const structuredData = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": currentPost.title,
        "description": currentPost.excerpt,
        "articleBody": currentPost.content.replace(/<[^>]*>/g, ''),
        "wordCount": wordCount,
        "timeRequired": `PT${currentPost.readTime.split(' ')[0]}M`,
        "author": {
          "@type": "Organization",
          "name": currentPost.author,
          "url": "https://nhsinterview.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Medical Interview Team",
          "logo": {
            "@type": "ImageObject",
            "url": "https://nhsinterview.com/logo.png"
          },
          "url": "https://nhsinterview.com"
        },
        "datePublished": currentPost.date,
        "dateModified": currentPost.date,
        "articleSection": currentPost.category,
        "url": `https://nhsinterview.com/blog/${slug}`,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://nhsinterview.com/blog/${slug}`
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://nhsinterview.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Blog",
              "item": "https://nhsinterview.com/blog"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": currentPost.title,
              "item": `https://nhsinterview.com/blog/${slug}`
            }
          ]
        },
        "keywords": [currentPost.category, currentPost.tag, "NHS Interview", "Medical Interview", "Doctor Interview Preparation", "NHS Application"].join(", "),
        "image": {
          "@type": "ImageObject",
          "url": "https://nhsinterview.com/og-image.png",
          "width": 1200,
          "height": 630,
          "caption": currentPost.title
        },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": [".prose h2", ".prose h3"]
        }
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-blog-post', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      // Reset title and meta tags when component unmounts
      document.title = "NHS Interview Prep 2024 | AI-Powered Mock Interviews for Doctors";
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', "Master your NHS medical interview with AI. Specialist prep for ST1, CT, and Consultant posts. Mapped to GMC Standards, Francis Report, and ISC Medical Interview frameworks.");
      }
    };
  }, [slug]);

  // Blog posts data with full content
  const blogPosts: BlogPostData[] = [
    {
      id: 27,
      slug: 'portfolio-station-where-evidence-is-tested-not-just-shown',
      category: 'Interview Insights',
      title: 'Portfolio Station: Where Evidence Is Tested, Not Just Shown',
      excerpt: 'Many ST interview processes include a dedicated portfolio station where the panel reviews your documents, asks targeted questions, and assesses how convincingly your achievements stand up to scrutiny.',
      author: 'Medical Interview Team',
      date: 'Feb 22, 2025',
      readTime: '6 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl border-l-4 border-cyan-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">📁</span>
            </div>
            <h3 class="text-lg font-bold text-cyan-800">Evidence Under Scrutiny</h3>
          </div>
          <p class="text-slate-700 italic">Many ST interview processes include a dedicated portfolio station where the panel reviews your documents, asks targeted questions, and assesses how convincingly your achievements stand up to scrutiny.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why This Station Matters Even When It Seems "Informal"
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Even in a relaxed setup, the panel will usually examine your CV carefully — especially anything you have listed as an achievement, such as publications, presentations, prizes, courses, or leadership roles. This station exists to confirm that:</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">✅</span>
              <span><strong>What you claim is accurate</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📝</span>
              <span><strong>What you did is clear</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🎯</span>
              <span><strong>Your contribution is credible</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📋</span>
              <span><strong>Your documentation is well organised</strong></span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">A weak portfolio station can quietly undermine a strong interview. A strong portfolio station can reinforce everything you say elsewhere.</p>

        <div class="bg-cyan-50 p-4 rounded-lg mb-8 border-l-4 border-cyan-400">
          <p class="text-cyan-800 font-medium">📁 <strong>Some candidates treat this as a tick-box formality. That is a mistake.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Bring Evidence, Not Just a Story
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">A portfolio station is not the place to rely on verbal explanations alone. You should bring supporting proof of what you have done, beyond the bare minimum requested.</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <p class="text-green-800 mb-3">Examples of helpful evidence include:</p>
          <ul class="space-y-2 text-green-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📄</span>
              <span>Copies of papers, posters, abstracts (simple A4 printouts are sufficient)</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🏆</span>
              <span>Certificates and formal letters confirming roles</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📊</span>
              <span>Logbook summaries or competency evidence (as appropriate to your specialty)</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🔬</span>
              <span>Audit or QI summaries and outcomes</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📝</span>
              <span>If you are writing a thesis: bring what you have completed so far</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">A completed thesis looks excellent — but even partial progress shows intent and momentum, which can strengthen how you are perceived.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          The Honesty Test: Exaggeration Can End Careers
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">One of the hidden purposes of this station is to identify inflated or misleading claims. Interviewers are aware that some candidates exaggerate. Panels have been known to verify publications and achievements independently.</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <p class="text-orange-800 mb-3">If your claims are accurate, you have nothing to fear.</p>
          <p class="text-orange-800 font-medium">If they are not, the consequences can be severe — including interview failure and escalation to professional regulators.</p>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">⚠️ <strong>The simplest rule is: only claim what you can defend with evidence.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          What the Panel Often Scores (and How You Win Marks)
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Scoring varies by specialty and deanery, but many portfolio stations share similar domains, often worth equal marks each.</p>

        <h3 class="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          <span class="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Oral Presentation and Communication (5 marks)
        </h3>
        <p class="text-slate-700 leading-relaxed mb-6">You may be asked to talk through:</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <ul class="space-y-2 text-blue-700">
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">💼</span>
              <span>A specific job or rotation</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">📁</span>
              <span>A portfolio section (teaching, audit, research, leadership)</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">⏰</span>
              <span>Your CV or personal summary within a strict time limit (often 2, 3, or 5 minutes)</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">High-scoring candidates sound:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-6">
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400 text-center">
            <div class="text-2xl mb-2">🌊</div>
            <h5 class="font-semibold text-green-800 mb-2">Fluent and confident</h5>
          </div>
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400 text-center">
            <div class="text-2xl mb-2">📋</div>
            <h5 class="font-semibold text-green-800 mb-2">Structured and coherent</h5>
          </div>
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400 text-center">
            <div class="text-2xl mb-2">👔</div>
            <h5 class="font-semibold text-green-800 mb-2">Professional and concise</h5>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Low scores are commonly linked to:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-red-50 p-4 rounded-lg border-l-4 border-red-400 text-center">
            <div class="text-2xl mb-2">🗣️</div>
            <h5 class="font-semibold text-red-800 mb-2">Rambling or disorganised</h5>
          </div>
          <div class="bg-red-50 p-4 rounded-lg border-l-4 border-red-400 text-center">
            <div class="text-2xl mb-2">📋</div>
            <h5 class="font-semibold text-red-800 mb-2">Weak structure</h5>
          </div>
          <div class="bg-red-50 p-4 rounded-lg border-l-4 border-red-400 text-center">
            <div class="text-2xl mb-2">👀</div>
            <h5 class="font-semibold text-red-800 mb-2">Poor eye contact</h5>
          </div>
        </div>

        <h3 class="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          <span class="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Interaction With the Panel (5 marks)
        </h3>
        <p class="text-slate-700 leading-relaxed mb-6">This domain reflects your ability to engage under questioning. The panel is watching for:</p>

        <div class="bg-purple-50 p-4 rounded-lg mb-6 border-l-4 border-purple-400">
          <ul class="space-y-2 text-purple-700">
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">👂</span>
              <span>Whether you listen properly</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">🎯</span>
              <span>Whether you answer what they asked</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">📝</span>
              <span>Whether your answers stay relevant</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">⚡</span>
              <span>Whether you can think quickly without losing clarity</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">High marks come from clear thinking and direct answers. Low marks come from confusion, irrelevance, or lack of coherence.</p>

        <h3 class="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          <span class="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Quality of Competence Evidence (5 marks)
        </h3>
        <p class="text-slate-700 leading-relaxed mb-6">Here the panel assesses the evidence itself, not just your explanation. They may review items such as logbooks, DOPs, mini-CEX, and related documentation depending on the specialty.</p>

        <p class="text-slate-700 leading-relaxed mb-6">They are judging:</p>

        <div class="grid md:grid-cols-4 gap-4 mb-8">
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400 text-center">
            <div class="text-2xl mb-2">📋</div>
            <h5 class="font-semibold text-emerald-800 mb-2">Organisation and layout</h5>
          </div>
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400 text-center">
            <div class="text-2xl mb-2">📄</div>
            <h5 class="font-semibold text-emerald-800 mb-2">Presentation and legibility</h5>
          </div>
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400 text-center">
            <div class="text-2xl mb-2">✅</div>
            <h5 class="font-semibold text-emerald-800 mb-2">Completeness</h5>
          </div>
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400 text-center">
            <div class="text-2xl mb-2">🧭</div>
            <h5 class="font-semibold text-emerald-800 mb-2">Ease of navigation</h5>
          </div>
        </div>

        <h3 class="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          <span class="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Quality of CV (5 marks)
        </h3>
        <p class="text-slate-700 leading-relaxed mb-6">Your CV is assessed for:</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📖</span>
              <span>Clarity and layout</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🎯</span>
              <span>Relevance of content</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">✅</span>
              <span>Completeness and professionalism</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📊</span>
              <span>Strength of supporting evidence</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">A clean, well-structured CV supported by proof of achievement scores strongly. A disorganised CV with unclear claims or weak evidence scores low — even if the candidate is competent.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">📁</span>
            The Portfolio Station in One Sentence
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">A portfolio station is not about impressing the panel with claims. It is about making your achievements easy to verify, easy to understand, and hard to doubt.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">📁</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 26,
      slug: 'how-frameworks-align-with-marking-schemes-positive-indicators',
      category: 'Interview Insights',
      title: 'How These Frameworks Align With Marking Schemes and Positive Indicators',
      excerpt: 'Frameworks are not simply communication aids. Their real power lies in how closely they align with how interviews are marked.',
      author: 'Medical Interview Team',
      date: 'Feb 20, 2025',
      readTime: '5 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-l-4 border-emerald-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">📊</span>
            </div>
            <h3 class="text-lg font-bold text-emerald-800">Frameworks and Scoring</h3>
          </div>
          <p class="text-slate-700 italic">Frameworks are not simply communication aids. Their real power lies in how closely they align with how interviews are marked.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Marking Schemes Reward Structure
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">In structured interviews, each question is designed to test one or more competencies. Interviewers listen with a checklist in mind, consciously or subconsciously asking:</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">💡</span>
              <span><strong>Has the candidate demonstrated insight?</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🎯</span>
              <span><strong>Have they shown appropriate judgement?</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🛡️</span>
              <span><strong>Are safety, professionalism, and reflection evident?</strong></span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Unstructured answers may contain all the right elements, but if those elements are not clearly signposted, they can be missed. Frameworks reduce this risk by making evidence visible.</p>

        <div class="bg-emerald-50 p-4 rounded-lg mb-8 border-l-4 border-emerald-400">
          <p class="text-emerald-800 font-medium">📊 <strong>In other words, frameworks do not just help you answer well — they help you be marked well.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          CAMP and Breadth-Based Marking
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Background and motivation questions are often scored on balance, realism, and alignment with the role.</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <p class="text-green-800 mb-3">CAMP aligns with positive indicators such as:</p>
          <ul class="space-y-2 text-green-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🏥</span>
              <span>Awareness of clinical development</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📚</span>
              <span>Engagement with education and evidence</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">⚙️</span>
              <span>Understanding of systems and leadership</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">👤</span>
              <span>Personal insight and sustainability</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Candidates who use CAMP rarely over-focus on a single domain. This balanced approach reassures interviewers that nothing important has been overlooked.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          STAR and Behavioural Scoring
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Skills-based questions are typically marked using observable behaviours rather than intentions or opinions.</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <p class="text-blue-800 mb-3">STAR aligns directly with this approach:</p>
          <ul class="space-y-2 text-blue-700">
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">📍</span>
              <span><strong>Situation/Task</strong> provides context</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">⚡</span>
              <span><strong>Action</strong> demonstrates skills and attributes</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">📊</span>
              <span><strong>Result/Reflection</strong> shows impact and learning</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Crucially, STAR encourages explanation of why decisions were made. This is where many positive indicators sit, such as insight, judgement, and self-awareness.</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-8 border-l-4 border-indigo-400">
          <p class="text-indigo-800 font-medium">🎯 <strong>Candidates who omit reflection often score as "satisfactory." Those who include it clearly are more likely to score "good" or "excellent."</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          SPIES and Decisive Indicators
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Questions involving difficult colleagues or ethical dilemmas often carry decisive negative indicators, particularly around patient safety.</p>

        <div class="bg-purple-50 p-4 rounded-lg mb-6 border-l-4 border-purple-400">
          <p class="text-purple-800 mb-3">SPIES is specifically designed to avoid these pitfalls by ensuring that:</p>
          <ul class="space-y-2 text-purple-700">
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">🛡️</span>
              <span>Patient safety is prioritised explicitly</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">📈</span>
              <span>Escalation is appropriate and timely</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">🤝</span>
              <span>Concerns are not handled in isolation</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">💝</span>
              <span>Compassion and support are considered</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Answers that fail to escalate serious concerns or that compromise safety can lead to very low scores or automatic failure. SPIES acts as a safeguard against these outcomes.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Making Interviewers' Jobs Easier — and Why That Matters
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers must score quickly and defensibly. When answers are structured:</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <ul class="space-y-2 text-orange-700">
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🔍</span>
              <span>Positive indicators are easier to identify</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">⚡</span>
              <span>Fewer assumptions are required</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">📊</span>
              <span>Confidence in scoring increases</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This does not advantage you unfairly; it allows your true competence to be recognised without distortion.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Frameworks Reduce Subjectivity
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Although interviews aim to be objective, some subjectivity is inevitable. Clear structure reduces this by:</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">👀</span>
              <span>Anchoring answers to observable behaviour</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">❓</span>
              <span>Minimising ambiguity</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🚫</span>
              <span>Preventing misinterpretation</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">The clearer your answer, the less room there is for doubt.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">📊</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Frameworks work because they mirror the logic of the marking process.</p>
          <p class="text-indigo-100 font-semibold">They help you cover all required domains, avoid decisive negative indicators, highlight positive behaviours clearly, and demonstrate judgement under pressure.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">📊</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 25,
      slug: 'spies-framework-handling-difficult-colleagues-high-risk-ethical-scenarios',
      category: 'Interview Insights',
      title: 'The SPIES Framework: Handling Difficult Colleagues and High-Risk Ethical Scenarios',
      excerpt: 'Questions about difficult colleagues are among the most emotionally challenging in medical interviews. They often involve hierarchy, conflict, and fear of repercussions.',
      author: 'Medical Interview Team',
      date: 'Feb 18, 2025',
      readTime: '6 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border-l-4 border-purple-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🕵️</span>
            </div>
            <h3 class="text-lg font-bold text-purple-800">Patient Safety First</h3>
          </div>
          <p class="text-slate-700 italic">Questions about difficult colleagues are among the most emotionally challenging in medical interviews. They often involve hierarchy, conflict, and fear of repercussions.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why These Questions Feel So Uncomfortable
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Scenarios involving unsafe or unprofessional colleagues trigger a natural internal conflict:</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">😨</span>
              <span><strong>Fear of consequences for yourself</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🤝</span>
              <span><strong>Loyalty to colleagues or seniors</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">❓</span>
              <span><strong>Uncertainty about escalation</strong></span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Interviewers understand this. What they are not asking is what you would personally feel comfortable doing. They are asking whether you understand what should be done in principle.</p>

        <div class="bg-purple-50 p-4 rounded-lg mb-8 border-l-4 border-purple-400">
          <p class="text-purple-800 font-medium">🕵️ <strong>This distinction is critical.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          What SPIES Stands For
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">SPIES is an acronym that guides your response through five essential priorities:</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <div class="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
            <div class="bg-white p-3 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-1">🔍</h4>
              <h4 class="font-bold text-blue-800 mb-1">Seek</h4>
              <h4 class="font-bold text-blue-800 text-sm">information</h4>
            </div>
            <div class="bg-white p-3 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-1">🛡️</h4>
              <h4 class="font-bold text-blue-800 mb-1">Patient</h4>
              <h4 class="font-bold text-blue-800 text-sm">safety</h4>
            </div>
            <div class="bg-white p-3 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-1">⚡</h4>
              <h4 class="font-bold text-blue-800 mb-1">Initiative</h4>
            </div>
            <div class="bg-white p-3 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-1">📈</h4>
              <h4 class="font-bold text-blue-800 mb-1">Escalate</h4>
            </div>
            <div class="bg-white p-3 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-1">🤝</h4>
              <h4 class="font-bold text-blue-800 mb-1">Support</h4>
            </div>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Together, these steps reflect a mature, systematic approach to risk, professionalism, and duty of care.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Seek Information: Understand the Problem First
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Before acting, you must understand the nature and seriousness of the issue. Not all problems are immediately clear-cut.</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <p class="text-green-800 mb-3">This may involve:</p>
          <ul class="space-y-2 text-green-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🔍</span>
              <span>Clarifying what you have observed</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">💬</span>
              <span>Speaking directly to the colleague, where appropriate</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📊</span>
              <span>Gaining context from others if necessary</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">In some cases — such as intoxication or immediate danger — this step is brief. In others, careful assessment is essential to avoid misjudgement.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Patient Safety: The Non-Negotiable Priority
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Once the situation is understood, patient safety comes first — always.</p>

        <div class="bg-red-50 p-4 rounded-lg mb-6 border-l-4 border-red-400">
          <p class="text-red-800 mb-3">If there is an immediate risk, action must be taken without delay. This may involve:</p>
          <ul class="space-y-2 text-red-700">
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">⏹️</span>
              <span>Stopping unsafe practice</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">🚪</span>
              <span>Removing the colleague from clinical duties</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">🛡️</span>
              <span>Ensuring patients are protected from harm</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Interviewers look very closely at this step. Any response that compromises patient safety, even indirectly, is likely to score poorly.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Initiative: What Can You Do Yourself?
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Where issues are minor and do not pose immediate risk, you may be able to take appropriate initial steps yourself. This might include:</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <ul class="space-y-2 text-blue-700">
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🤝</span>
              <span>Offering support or guidance</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">💬</span>
              <span>Addressing behaviour constructively</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">👨‍🏫</span>
              <span>Coaching or informal discussion</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">However, this step has limits. Initiative does not mean handling serious problems alone.</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-8 border-l-4 border-orange-400">
          <p class="text-orange-800 font-medium">🎯 <strong>Recognising when something is beyond your remit is a sign of maturity, not weakness.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Escalate: Involving the Right People
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">When a situation exceeds your authority, experience, or ability to resolve it safely, escalation is essential.</p>

        <div class="bg-purple-50 p-4 rounded-lg mb-6 border-l-4 border-purple-400">
          <p class="text-purple-800 mb-3">This may involve:</p>
          <ul class="space-y-2 text-purple-700">
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">👨‍⚕️</span>
              <span>Senior colleagues</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">👨‍🏫</span>
              <span>Educational or clinical supervisors</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">🏢</span>
              <span>Departmental or organisational leadership</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">In severe cases, external bodies may need to be involved. Interviewers are not looking for bravery; they are looking for appropriate escalation.</p>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">⚠️ <strong>Failure to escalate when necessary is a serious concern.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">7</span>
          Support: Looking After People and Teams
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">SPIES does not end with reporting. Colleagues involved in these situations often require support, and teams may be affected by disruption or stress.</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <p class="text-pink-800 mb-3">A balanced answer acknowledges:</p>
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🤝</span>
              <span>The colleague's need for help or remediation</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">👥</span>
              <span>The impact on the wider team</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">⚖️</span>
              <span>The importance of maintaining professionalism and dignity</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This demonstrates compassion without compromising safety.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">8</span>
          "Should" Versus "Would": A Crucial Distinction
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers expect you to describe what should happen in an ideal professional setting — not what you might personally do if you were afraid or uncertain.</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 mb-3">This allows them to assess your understanding of:</p>
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">⚖️</span>
              <span>Ethical principles</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🎯</span>
              <span>Professional responsibilities</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📋</span>
              <span>Regulatory expectations</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Hesitation or over-emphasis on personal risk often weakens otherwise good answers.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">9</span>
          Why SPIES Works
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">SPIES works because it:</p>

        <div class="bg-cyan-50 p-4 rounded-lg mb-6 border-l-4 border-cyan-400">
          <ul class="space-y-2 text-cyan-700">
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">🛡️</span>
              <span>Forces patient safety to the forefront</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">📋</span>
              <span>Prevents impulsive or incomplete responses</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">⚖️</span>
              <span>Aligns with professional guidance and expectations</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">🎯</span>
              <span>Demonstrates balanced judgement</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">It ensures that even in extreme scenarios, your answer remains structured, ethical, and defensible.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🕵️</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Questions about difficult colleagues are not about courage or confrontation. They are about judgement, responsibility, and professionalism.</p>
          <p class="text-indigo-100 font-semibold">SPIES ensures that patients are protected, colleagues are treated fairly, systems are used appropriately, and you are seen as a safe, reliable doctor.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">🕵️</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 24,
      slug: 'star-framework-turning-experience-into-scorable-evidence',
      category: 'Interview Insights',
      title: 'The STAR Framework: Turning Experience into Scorable Evidence',
      excerpt: 'Experience alone does not score marks at interview. What matters is how clearly you present that experience, how well you explain your reasoning.',
      author: 'Medical Interview Team',
      date: 'Feb 16, 2025',
      readTime: '6 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-l-4 border-blue-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">⭐</span>
            </div>
            <h3 class="text-lg font-bold text-blue-800">Experience into Evidence</h3>
          </div>
          <p class="text-slate-700 italic">Experience alone does not score marks at interview. What matters is how clearly you present that experience, how well you explain your reasoning.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          When STAR Should Be Used
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">STAR is designed for questions that explicitly ask for an example, such as:</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">⏱️</span>
              <span><strong>"Tell us about a time you worked under pressure"</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">😠</span>
              <span><strong>"Describe a situation where you dealt with a difficult patient"</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📞</span>
              <span><strong>"Give an example of when you had to ask for senior help"</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">💬</span>
              <span><strong>"Tell us about a situation where your communication made a difference"</strong></span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">These questions are not invitations to general discussion. They are requests for observable behaviour.</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-8 border-l-4 border-blue-400">
          <p class="text-blue-800 font-medium">⭐ <strong>STAR turns lived experience into evidence.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          What STAR Stands For
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">STAR breaks an example into four logical components:</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div class="bg-white p-4 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-2">📍</h4>
              <h4 class="font-bold text-blue-800 mb-2">Situation</h4>
              <h4 class="font-bold text-blue-800 mb-2">/</h4>
              <h4 class="font-bold text-blue-800 mb-2">Task</h4>
            </div>
            <div class="bg-white p-4 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-2">⚡</h4>
              <h4 class="font-bold text-blue-800 mb-2">Action</h4>
            </div>
            <div class="bg-white p-4 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-2">📊</h4>
              <h4 class="font-bold text-blue-800 mb-2">Result</h4>
            </div>
            <div class="bg-white p-4 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-2">💭</h4>
              <h4 class="font-bold text-blue-800 mb-2">Reflection</h4>
            </div>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Although simple in appearance, STAR is powerful because it mirrors how interviewers are trained to listen and score.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Step 1: Situation or Task — Setting the Scene (Briefly)
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The purpose of the Situation or Task is to provide just enough context for the interviewer to understand what follows. This section should be:</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <ul class="space-y-2 text-green-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">⏱️</span>
              <span><strong>Concise</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🎯</span>
              <span><strong>Relevant</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📋</span>
              <span><strong>Focused only on what is necessary</strong></span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">It is not the main event. Overloading this section with background detail wastes time and dilutes impact.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Step 2: Action — The Core of the Answer
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The Action section is where most of the marks are earned.</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <p class="text-blue-800 mb-3">This is where interviewers assess whether you possess the skills and attributes being tested. To do this effectively, you must:</p>
          <ul class="space-y-2 text-blue-700">
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">👤</span>
              <span>Speak in the first person</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">📝</span>
              <span>Be explicit about your role</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">💭</span>
              <span>Explain not only what you did, but how and why</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Simply describing actions without rationale often results in answers that feel superficial or accidental.</p>

        <h3 class="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          <span class="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">?</span>
          Why "Why" Matters
        </h3>
        <p class="text-slate-700 leading-relaxed mb-6">Candidates frequently explain what they did and how they did it, but omit why. This is a critical error.</p>

        <div class="bg-purple-50 p-4 rounded-lg mb-6 border-l-4 border-purple-400">
          <p class="text-purple-800 mb-3">Explaining why you acted as you did demonstrates:</p>
          <ul class="space-y-2 text-purple-700">
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">💡</span>
              <span><strong>Insight</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">🎯</span>
              <span><strong>Intentional decision-making</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">📈</span>
              <span><strong>Understanding of consequences</strong></span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Without this, interviewers may struggle to distinguish judgement from luck.</p>

        <h3 class="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          <span class="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">⚠️</span>
          Avoiding Common STAR Pitfalls
        </h3>
        <p class="text-slate-700 leading-relaxed mb-6">Even when STAR is used, answers can still underperform if:</p>

        <div class="bg-red-50 p-4 rounded-lg mb-6 border-l-4 border-red-400">
          <ul class="space-y-2 text-red-700">
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">👥</span>
              <span>The focus drifts to other people rather than you</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">📊</span>
              <span>Clinical detail overwhelms the message</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">🎯</span>
              <span>The story lacks clear purpose</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">❌</span>
              <span>The ending is missing</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">A STAR answer without a conclusion leaves interviewers uncertain and often prompts unnecessary follow-up.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Step 3: Result and Reflection — Closing the Loop
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Strong candidates always bring their answer to a clear end.</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <p class="text-orange-800 mb-3">You can conclude in one of two effective ways:</p>
          <ul class="space-y-2 text-orange-700">
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">📊</span>
              <span><strong>Outcome-focused:</strong> explaining what happened and why it mattered</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">💭</span>
              <span><strong>Reflection-focused:</strong> summarising what the experience taught you and how it influenced your practice</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Reflection elevates answers from descriptive to developmental. It signals growth, maturity, and self-awareness.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Why STAR Works So Well
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">STAR works because it:</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📋</span>
              <span>Presents information in a logical sequence</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🔍</span>
              <span>Makes behaviours easy to identify</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🎯</span>
              <span>Aligns naturally with marking schemes</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">❓</span>
              <span>Reduces ambiguity for interviewers</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Many interviewers are trained in STAR or similar models. Even when they are not, they instinctively recognise its clarity.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">7</span>
          A STAR Example in Action
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">To see STAR in practice, let's apply it to a common interview question:</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 font-medium mb-3"><strong>Question:</strong> "Tell us about a time you worked under pressure"</p>

          <div class="space-y-4">
            <div class="bg-white p-4 rounded-lg border-l-4 border-green-400">
              <h5 class="font-semibold text-green-800 mb-2">📍 <strong>Situation:</strong></h5>
              <p class="text-green-700 text-sm">During my foundation year, I was working in A&E when we received three critically ill patients simultaneously from a major road traffic collision, while also managing a busy department with only limited senior support available.</p>
            </div>

            <div class="bg-white p-4 rounded-lg border-l-4 border-blue-400">
              <h5 class="font-semibold text-blue-800 mb-2">⚡ <strong>Action:</strong></h5>
              <p class="text-blue-700 text-sm">I quickly assessed the most urgent case requiring immediate intervention, delegated the second most critical patient to the available registrar, and coordinated with nursing staff to ensure the third patient was stabilised. I communicated clearly with the trauma team, prioritised tasks based on ABC principles, and sought senior input when needed rather than delaying care.</p>
            </div>

            <div class="bg-white p-4 rounded-lg border-l-4 border-orange-400">
              <h5 class="font-semibold text-orange-800 mb-2">📊 <strong>Result:</strong></h5>
              <p class="text-orange-700 text-sm">All three patients were stabilised within the golden hour, with no adverse outcomes. The department maintained flow, and the nursing team commended the coordinated approach.</p>
            </div>

            <div class="bg-white p-4 rounded-lg border-l-4 border-purple-400">
              <h5 class="font-semibold text-purple-800 mb-2">💭 <strong>Reflection:</strong></h5>
              <p class="text-purple-700 text-sm">This experience reinforced the importance of maintaining composure under pressure and taught me that effective leadership in crisis involves clear communication, rapid prioritisation, and knowing when to seek help. It shaped how I approach busy clinical situations today.</p>
            </div>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Notice how this example demonstrates: clear context without rambling, first-person ownership of actions, explanation of reasoning ("why"), and meaningful reflection that shows growth.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">8</span>
          Using STAR Flexibly
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">STAR is a guide, not a script. You do not need to label each section explicitly. With practice, the structure becomes invisible, and your answer sounds natural while remaining organised.</p>

        <div class="bg-emerald-50 p-4 rounded-lg mb-6 border-l-4 border-emerald-400">
          <p class="text-emerald-800 font-medium">🎯 <strong>The goal is not perfection — it is clarity under pressure.</strong></p>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">⭐</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Interviewers are not impressed by dramatic stories. They are impressed by clear thinking, purposeful action, and honest reflection.</p>
          <p class="text-indigo-100 font-semibold">STAR ensures that your experience is not just heard, but understood — and scored.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">⭐</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 23,
      slug: 'camp-framework-structuring-background-motivation-answers',
      category: 'Interview Insights',
      title: 'The CAMP Framework: Structuring Background and Motivation Answers Without Rambling',
      excerpt: 'Background and motivation questions are deceptively difficult. They feel open, familiar, and conversational — yet they are some of the most poorly answered questions.',
      author: 'Medical Interview Team',
      date: 'Feb 14, 2025',
      readTime: '5 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border-l-4 border-green-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🏕️</span>
            </div>
            <h3 class="text-lg font-bold text-green-800">Balance, Breadth, and Control</h3>
          </div>
          <p class="text-slate-700 italic">Background and motivation questions are deceptively difficult. They feel open, familiar, and conversational — yet they are some of the most poorly answered questions.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Background Questions Are High-Risk
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Questions such as:</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">❓</span>
              <span><strong>"Tell me about yourself"</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📄</span>
              <span><strong>"Take me through your CV"</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🎯</span>
              <span><strong>"Why do you want to train in this deanery or specialty?"</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📈</span>
              <span><strong>"How do you see your career developing?"</strong></span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">are not asking for a life story. They are structured prompts designed to assess whether your experience, interests, and motivations align logically with the role.</p>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 mb-3 font-medium">Without a framework, candidates often:</p>
          <ul class="space-y-2 text-red-700">
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">⚠️</span>
              <span><strong>Focus excessively on clinical work and ignore academic or management experience</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">📖</span>
              <span><strong>Drift into personal anecdotes with little relevance</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">⏱️</span>
              <span><strong>Speak at length without a clear endpoint</strong></span>
            </li>
          </ul>
        </div>

        <div class="bg-green-50 p-4 rounded-lg mb-8 border-l-4 border-green-400">
          <p class="text-green-800 font-medium">🏕️ <strong>CAMP solves this by ensuring coverage without excess.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          What CAMP Stands For
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">CAMP is an acronym representing four complementary domains:</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div class="bg-white p-4 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-2">🏥</h4>
              <h4 class="font-bold text-blue-800 mb-2">Clinical</h4>
            </div>
            <div class="bg-white p-4 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-2">📚</h4>
              <h4 class="font-bold text-blue-800 mb-2">Academic</h4>
            </div>
            <div class="bg-white p-4 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-2">⚙️</h4>
              <h4 class="font-bold text-blue-800 mb-2">Management</h4>
            </div>
            <div class="bg-white p-4 rounded-lg">
              <h4 class="font-bold text-blue-800 mb-2">👤</h4>
              <h4 class="font-bold text-blue-800 mb-2">Personal</h4>
            </div>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Together, these domains reflect the broad expectations placed on doctors at all levels. Using CAMP signals that you understand medicine as a multi-dimensional profession, not just a technical one.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Clinical: Demonstrating Professional Direction
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The clinical component addresses how you see yourself developing as a clinician. This may include:</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <ul class="space-y-2 text-green-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🏥</span>
              <span>The type of clinical environment you are drawn to</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🎯</span>
              <span>Specific skills or subspecialty interests</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">👥</span>
              <span>The patient populations you wish to work with</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">The aim is not to sound over-specialised, but to show thoughtful progression and realism.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Academic: Showing Engagement Beyond Service
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The academic domain allows you to demonstrate curiosity, teaching interest, and engagement with evidence-based practice. This can include:</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <ul class="space-y-2 text-blue-700">
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🔬</span>
              <span>Research involvement</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">👨‍🏫</span>
              <span>Teaching experience</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">📊</span>
              <span>Educational interests or qualifications</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🏛️</span>
              <span>Contributions at regional or professional body level</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Even candidates without formal research backgrounds can demonstrate academic engagement through teaching, audit, or guideline work.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Management: Reflecting System Awareness
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Medicine does not operate in isolation from systems. The management component highlights your understanding of how care is delivered and improved. This may involve:</p>

        <div class="bg-purple-50 p-4 rounded-lg mb-6 border-l-4 border-purple-400">
          <ul class="space-y-2 text-purple-700">
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">📈</span>
              <span>Audit and quality improvement</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">🔧</span>
              <span>Service development</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">🛡️</span>
              <span>Risk management</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">👨‍💼</span>
              <span>Educational or supervisory roles</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Including management shows that you appreciate responsibilities beyond direct patient contact.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Personal: Humanising Your Narrative
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The personal domain grounds your answer in realism and authenticity. It may include:</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">📍</span>
              <span>Geographic preferences</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">👨‍👩‍👧‍👦</span>
              <span>Life plans that influence career decisions</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">✈️</span>
              <span>Time abroad or non-clinical experiences</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🎨</span>
              <span>Interests that contribute to resilience or teamwork</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This is not filler. Used appropriately, it helps interviewers see you as a sustainable colleague, not just a capable one.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">7</span>
          Why CAMP Works So Well
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">CAMP works because it:</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <ul class="space-y-2 text-orange-700">
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">⚖️</span>
              <span>Prevents overemphasis on one area</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">📋</span>
              <span>Creates a natural structure for broad questions</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">⏱️</span>
              <span>Keeps answers coherent and time-limited</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🎯</span>
              <span>Aligns with how interviewers conceptualise professional development</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">It also gives you confidence. When you know you have four clear domains to cover, you are far less likely to freeze or ramble.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">8</span>
          Using CAMP Intelligently
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">CAMP is not a checklist. You do not need to give equal time to each domain in every answer. The weighting should reflect:</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🎯</span>
              <span>The role you are applying for</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">❓</span>
              <span>The question being asked</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📊</span>
              <span>Your actual experience</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">The framework guides your thinking; it should not dominate your delivery.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🏕️</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Background and motivation questions are not about saying everything. They are about saying the right things, in the right balance.</p>
          <p class="text-indigo-100 font-semibold">CAMP allows you to present a coherent professional narrative that is structured, relevant, and memorable.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">🏕️</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 22,
      slug: 'three-core-interview-frameworks-camp-star-spies',
      category: 'Interview Insights',
      title: 'The Three Core Interview Frameworks: When and Why to Use CAMP, STAR, and SPIES',
      excerpt: 'Once you understand why frameworks matter, the next step is knowing which framework to use, and when. Structured interviews are not random conversations.',
      author: 'Medical Interview Team',
      date: 'Feb 12, 2025',
      readTime: '6 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl border-l-4 border-blue-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🏗️</span>
            </div>
            <h3 class="text-lg font-bold text-blue-800">Precision in Structure</h3>
          </div>
          <p class="text-slate-700 italic">Once you understand why frameworks matter, the next step is knowing which framework to use, and when. Structured interviews are not random conversations.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Not All Questions Are the Same — and Neither Are the Answers
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interview questions generally fall into a small number of predictable categories. Some explore your background and motivation, others probe your skills through examples, and some test judgement in ethically or emotionally challenging situations.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 mb-3 font-medium">Each category demands a different type of thinking. Trying to answer all questions with the same narrative style often leads to:</p>
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🗣️</span>
              <span><strong>Rambling answers</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">❌</span>
              <span><strong>Missed competencies</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📊</span>
              <span><strong>Poor alignment with marking criteria</strong></span>
            </li>
          </ul>
        </div>

        <div class="bg-blue-50 p-4 rounded-lg mb-8 border-l-4 border-blue-400">
          <p class="text-blue-800 font-medium">🎯 <strong>Frameworks solve this by matching question type to answer structure.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          CAMP, STAR, and SPIES: Three Purposes, Three Mindsets
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The three core frameworks introduced in this section are not interchangeable. Each exists to serve a specific function in the interview.</p>

        <div class="grid md:grid-cols-3 gap-6 mb-8">
          <div class="bg-green-50 p-6 rounded-xl border-l-4 border-green-400">
            <h4 class="font-bold text-green-800 mb-3 flex items-center gap-2">
              <span class="text-green-600 text-xl">🏕️</span>
              CAMP
            </h4>
            <p class="text-green-700 mb-3">is designed for breadth.</p>
            <p class="text-green-700 text-sm">It helps you organise wide-ranging information about your background, motivation, and future direction without losing balance or coherence.</p>
          </div>

          <div class="bg-blue-50 p-6 rounded-xl border-l-4 border-blue-400">
            <h4 class="font-bold text-blue-800 mb-3 flex items-center gap-2">
              <span class="text-blue-600 text-xl">⭐</span>
              STAR
            </h4>
            <p class="text-blue-700 mb-3">is designed for depth.</p>
            <p class="text-blue-700 text-sm">It allows you to present specific experiences in a way that highlights skills, judgement, and reflection — all of which are central to scoring.</p>
          </div>

          <div class="bg-purple-50 p-6 rounded-xl border-l-4 border-purple-400">
            <h4 class="font-bold text-purple-800 mb-3 flex items-center gap-2">
              <span class="text-purple-600 text-xl">🕵️</span>
              SPIES
            </h4>
            <p class="text-purple-700 mb-3">is designed for safety and ethics.</p>
            <p class="text-purple-700 text-sm">It ensures that when discussing difficult colleagues or serious concerns, you consistently prioritise patient safety, professionalism, and appropriate escalation.</p>
          </div>
        </div>

        <div class="bg-indigo-50 p-4 rounded-lg mb-8 border-l-4 border-indigo-400">
          <p class="text-indigo-800 font-medium">🎯 <strong>Understanding this distinction is crucial. Strong candidates do not choose frameworks randomly — they choose them intentionally.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Why Interviewers Respond Well to These Structures
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Many interviewers have been formally trained using similar frameworks, particularly STAR. Even when they have not, structured answers are easier to follow, easier to score, and easier to trust.</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <p class="text-orange-800 mb-3">When information is presented in a logical sequence:</p>
          <ul class="space-y-2 text-orange-700">
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">👂</span>
              <span>Interviewers become more receptive</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🔍</span>
              <span>Key behaviours are easier to identify</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">✅</span>
              <span>Positive indicators stand out more clearly</span>
            </li>
          </ul>
        </div>

        <div class="bg-teal-50 p-4 rounded-lg mb-8 border-l-4 border-teal-400">
          <p class="text-teal-800 font-medium">🎯 <strong>This does not make your answers artificial; it makes them accessible.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Flexibility Is More Important Than Perfection
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">These frameworks are guides, not scripts. Interviewers are not checking whether you name each step correctly. What matters is whether your answer:</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🎯</span>
              <span>Covers the relevant domains</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">💭</span>
              <span>Demonstrates insight and judgement</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">📋</span>
              <span>Feels coherent and complete</span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">⚠️ <strong>Overly rigid use can sound mechanical. Intelligent use sounds natural because the structure supports — rather than dominates — your thinking.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Preparation Turns Frameworks into Instinct
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The real value of frameworks emerges only with practice. With repetition:</p>

        <div class="bg-emerald-50 p-4 rounded-lg mb-6 border-l-4 border-emerald-400">
          <ul class="space-y-2 text-emerald-700">
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">🧠</span>
              <span>You stop consciously recalling steps</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">⚡</span>
              <span>You start thinking in structured patterns</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">🌊</span>
              <span>Your answers become calmer and more fluid</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This is particularly important under stress, when unstructured thinking tends to collapse.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          How These Frameworks Will Be Used Going Forward
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Throughout the remainder of this material, these three frameworks will be applied repeatedly:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-cyan-50 p-4 rounded-lg border-l-4 border-cyan-400 text-center">
            <div class="text-2xl mb-2">🏥</div>
            <h5 class="font-semibold text-cyan-800 mb-2">To different specialties</h5>
            <p class="text-cyan-700 text-sm">and contexts</p>
          </div>
          <div class="bg-cyan-50 p-4 rounded-lg border-l-4 border-cyan-400 text-center">
            <div class="text-2xl mb-2">📈</div>
            <h5 class="font-semibold text-cyan-800 mb-2">To different grades</h5>
            <p class="text-cyan-700 text-sm">and levels</p>
          </div>
          <div class="bg-cyan-50 p-4 rounded-lg border-l-4 border-cyan-400 text-center">
            <div class="text-2xl mb-2">❓</div>
            <h5 class="font-semibold text-cyan-800 mb-2">To a wide variety of</h5>
            <p class="text-cyan-700 text-sm">questions</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">You will also see how they can be adapted and refined for specific scenarios. More importantly, you will learn how to think logically about questions, even when no obvious framework comes to mind.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🏗️</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Frameworks are not there to limit your answers. They are there to ensure that your best thinking survives pressure.</p>
          <p class="text-indigo-100 font-semibold">By choosing the right framework for the right question, you align your answers with how interviews are actually designed — and how candidates are actually scored.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">🏗️</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 21,
      slug: 'why-frameworks-matter-interviews',
      category: 'Interview Insights',
      title: 'Why Frameworks Matter in Interviews',
      excerpt: 'Medical interviews are not designed to reward improvisation. They reward clarity under pressure. When candidates struggle, it is rarely because they lack experience or insight.',
      author: 'Medical Interview Team',
      date: 'Feb 10, 2025',
      readTime: '5 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl border-l-4 border-purple-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🗂️</span>
            </div>
            <h3 class="text-lg font-bold text-purple-800">Thinking Tools for High Pressure</h3>
          </div>
          <p class="text-slate-700 italic">Medical interviews are not designed to reward improvisation. They reward clarity under pressure. When candidates struggle, it is rarely because they lack experience or insight.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Interviews Test Thinking, Not Memory
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">At interview, you are expected to process unfamiliar questions, recall relevant experiences, prioritise information, and communicate clearly — all in a limited time and under scrutiny. Relying on memory alone is risky. Anxiety can cause even well-prepared candidates to blank, ramble, or lose direction.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 font-medium">Frameworks reduce this cognitive load. They provide a reliable mental scaffold that allows you to focus on content rather than panic about structure.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Structure Creates Direction and Reassurance
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Knowing that you have a clear structure to fall back on gives you:</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <ul class="space-y-2 text-blue-700">
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🧭</span>
              <span>A sense of direction when a question feels broad or unexpected</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🛡️</span>
              <span>Reassurance that you are covering what interviewers are looking for</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">⚡</span>
              <span>Greater control over pacing and emphasis</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This sense of control is visible to interviewers. It manifests as calm delivery, logical sequencing, and confident presence — all of which influence scoring, even when not explicitly marked.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Frameworks Are Transferable Across Questions
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">One of the greatest advantages of structured frameworks is their reusability. Once internalised, they can be applied repeatedly across a wide range of questions and scenarios. You are no longer preparing answers question by question; instead, you are preparing ways of thinking.</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <p class="text-green-800 font-medium">This allows you to respond effectively even when faced with questions you did not anticipate.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Memorisation Is Not the Goal
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Frameworks are often misunderstood as templates to be memorised and reproduced verbatim. This is a mistake. At interview, rigid recall increases anxiety and reduces flexibility.</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 mb-3">The goal is not memorisation, but familiarity.</p>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">With sufficient preparation, these structures become second nature. When that happens:</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-8 border-l-4 border-indigo-400">
          <ul class="space-y-2 text-indigo-700">
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">🧠</span>
              <span>You spend less effort recalling what to say</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">💭</span>
              <span>You think more clearly in the moment</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">🗣️</span>
              <span>Your answers sound natural rather than rehearsed</span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Frameworks Support Common Sense, Not Replace It
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Good interview answers are grounded in common sense and real experience. Frameworks do not override this — they protect it. They ensure that under pressure you do not forget essential elements such as:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-pink-50 p-4 rounded-lg border-l-4 border-pink-400 text-center">
            <div class="text-2xl mb-2">📍</div>
            <h5 class="font-semibold text-pink-800 mb-2">Context</h5>
            <p class="text-pink-700 text-sm">where things happened</p>
          </div>
          <div class="bg-pink-50 p-4 rounded-lg border-l-4 border-pink-400 text-center">
            <div class="text-2xl mb-2">👤</div>
            <h5 class="font-semibold text-pink-800 mb-2">Your personal role</h5>
            <p class="text-pink-700 text-sm">in the situation</p>
          </div>
          <div class="bg-pink-50 p-4 rounded-lg border-l-4 border-pink-400 text-center">
            <div class="text-2xl mb-2">💡</div>
            <h5 class="font-semibold text-pink-800 mb-2">Rationale behind</h5>
            <p class="text-pink-700 text-sm">your decisions</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">In other words, frameworks stop good judgement from being lost to stress.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          From Technique to Professional Skill
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The ability to structure information clearly is not just an interview skill. It reflects how doctors think and communicate in clinical practice:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400 text-center">
            <div class="text-2xl mb-2">📋</div>
            <h5 class="font-semibold text-emerald-800 mb-2">When handing over</h5>
            <p class="text-emerald-700 text-sm">patients</p>
          </div>
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400 text-center">
            <div class="text-2xl mb-2">💬</div>
            <h5 class="font-semibold text-emerald-800 mb-2">When explaining</h5>
            <p class="text-emerald-700 text-sm">plans</p>
          </div>
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400 text-center">
            <div class="text-2xl mb-2">⚖️</div>
            <h5 class="font-semibold text-emerald-800 mb-2">When managing</h5>
            <p class="text-emerald-700 text-sm">conflict</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Interviewers recognise this. Structured answers signal readiness for responsibility.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🗂️</span>
            The Core Principle
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Frameworks work because they align with how interviews are designed to assess candidates. They help you stay focused, remain calm, communicate with clarity, and demonstrate insight rather than recite information.</p>
          <p class="text-indigo-100 font-semibold">Used intelligently, they do not constrain your answers — they liberate them.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">🗂️</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 20,
      slug: 'psychology-positivity-selling-what-you-have',
      category: 'Interview Insights',
      title: 'The Psychology of Positivity: Why Selling What You Have Matters',
      excerpt: 'Interviews are not neutral environments. They are evaluative, comparative, and time-limited. In this setting, the emotional tone of your answers matters more than many candidates realise.',
      author: 'Medical Interview Team',
      date: 'Feb 07, 2025',
      readTime: '4 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-emerald-50 to-lime-50 rounded-2xl border-l-4 border-emerald-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">☀️</span>
            </div>
            <h3 class="text-lg font-bold text-emerald-800">Emotional Tone in Evaluation</h3>
          </div>
          <p class="text-slate-700 italic">Interviews are not neutral environments. They are evaluative, comparative, and time-limited. In this setting, the emotional tone of your answers matters more than many candidates realise.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why First Impressions Are Shaped by Tone
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers form early impressions about confidence, motivation, and fit. These impressions are influenced not only by content, but by how candidates frame their experiences.</p>

        <div class="bg-red-50 p-4 rounded-lg mb-6 border-l-4 border-red-400">
          <p class="text-red-800 mb-3 font-medium">Answers that begin with:</p>
          <div class="space-y-2">
            <div class="p-2 bg-white rounded">
              <p class="text-red-700 italic">"I haven't done much..."</p>
            </div>
            <div class="p-2 bg-white rounded">
              <p class="text-red-700 italic">"I'm only average at..."</p>
            </div>
            <div class="p-2 bg-white rounded">
              <p class="text-red-700 italic">"I don't really have a lot of experience in..."</p>
            </div>
          </div>
        </div>

        <div class="bg-slate-50 p-4 rounded-lg mb-8 border-l-4 border-slate-400">
          <p class="text-slate-800 font-medium">⚠️ <strong>immediately lower expectations. Even if the rest of the answer is solid, recovery is difficult.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Selling What You Have Is Not Boasting
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Many candidates worry that presenting themselves positively will sound arrogant. In reality, interviewers expect candidates to highlight their strengths clearly and honestly.</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <p class="text-blue-800 mb-3">Selling what you have means:</p>
          <ul class="space-y-2 text-blue-700">
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🎯</span>
              <span>Focusing on experience you do possess</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">💪</span>
              <span>Describing it confidently and accurately</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🚫</span>
              <span>Avoiding unnecessary self-criticism</span>
            </li>
          </ul>
        </div>

        <div class="bg-green-50 p-4 rounded-lg mb-8 border-l-4 border-green-400">
          <p class="text-green-800 font-medium">✅ <strong>It does not mean pretending to be perfect.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          How Negativity Undermines Otherwise Strong Answers
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Negative framing often comes from nerves rather than lack of ability. Unfortunately, interviewers cannot separate the two.</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <p class="text-orange-800 mb-3">Negative openings:</p>
          <ul class="space-y-2 text-orange-700">
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🎯</span>
              <span>Distract from strengths</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">📉</span>
              <span>Reduce perceived motivation</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">⚠️</span>
              <span>Trigger cautious scoring</span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">❌ <strong>Once a negative tone is set, interviewers listen more critically.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Confidence Signals Readiness
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Positive framing signals that you:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-400 text-center">
            <div class="text-2xl mb-2">💪</div>
            <h5 class="font-semibold text-teal-800 mb-2">Believe in your</h5>
            <p class="text-teal-700 text-sm">preparation</p>
          </div>
          <div class="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-400 text-center">
            <div class="text-2xl mb-2">🎯</div>
            <h5 class="font-semibold text-teal-800 mb-2">Understand your</h5>
            <p class="text-teal-700 text-sm">value</p>
          </div>
          <div class="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-400 text-center">
            <div class="text-2xl mb-2">🚀</div>
            <h5 class="font-semibold text-teal-800 mb-2">Are ready for</h5>
            <p class="text-teal-700 text-sm">progression</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This is especially important for senior roles, where confidence and self-belief are considered part of professional readiness.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Staying Positive While Remaining Honest
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Positivity should never come at the expense of honesty. Strong candidates:</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">✅</span>
              <span>Acknowledge limitations when appropriate</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">📈</span>
              <span>Pair them with learning and growth</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🚫</span>
              <span>Avoid apologetic language</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">For example, rather than focusing on what you lack, focus on how you are developing.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Positivity and Professional Identity
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers are not just selecting for competence. They are selecting colleagues. A positive, constructive mindset suggests someone who will:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 text-center">
            <div class="text-2xl mb-2">🎯</div>
            <h5 class="font-semibold text-purple-800 mb-2">Engage with</h5>
            <p class="text-purple-700 text-sm">challenges</p>
          </div>
          <div class="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 text-center">
            <div class="text-2xl mb-2">🔄</div>
            <h5 class="font-semibold text-purple-800 mb-2">Respond well to</h5>
            <p class="text-purple-700 text-sm">feedback</p>
          </div>
          <div class="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 text-center">
            <div class="text-2xl mb-2">🤝</div>
            <h5 class="font-semibold text-purple-800 mb-2">Contribute to team</h5>
            <p class="text-purple-700 text-sm">morale</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">These traits matter, even if they are not explicitly listed in marking schemes.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">☀️</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Interviews reward candidates who can present themselves clearly, value their own experience, and speak with confidence and balance.</p>
          <p class="text-indigo-100 font-semibold">If you do not believe in what you bring to the role, interviewers will struggle to believe it too.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">☀️</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 19,
      slug: 'precision-over-padding-vagueness-detail',
      category: 'Interview Insights',
      title: 'Precision Over Padding: Avoiding Vagueness and Unnecessary Detail',
      excerpt: 'In interviews, more information does not automatically mean better answers. In fact, excessive detail often weakens otherwise strong responses.',
      author: 'Medical Interview Team',
      date: 'Feb 05, 2025',
      readTime: '4 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl border-l-4 border-indigo-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🎯</span>
            </div>
            <h3 class="text-lg font-bold text-indigo-800">The Art of Selective Communication</h3>
          </div>
          <p class="text-slate-700 italic">In interviews, more information does not automatically mean better answers. In fact, excessive detail often weakens otherwise strong responses.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Vagueness Lowers Scores
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Vague statements sound safe, but they are difficult to score. Phrases such as:</p>

        <div class="bg-red-50 p-4 rounded-lg mb-6 border-l-4 border-red-400">
          <div class="space-y-2">
            <div class="p-2 bg-white rounded">
              <p class="text-red-700 italic">"I enjoy surgery"</p>
            </div>
            <div class="p-2 bg-white rounded">
              <p class="text-red-700 italic">"I'm interested in research"</p>
            </div>
            <div class="p-2 bg-white rounded">
              <p class="text-red-700 italic">"I have good communication skills"</p>
            </div>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">tell interviewers very little unless they are supported by specific facts.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-8 border-l-4 border-slate-400">
          <p class="text-slate-800 font-medium">💭 <strong>Vagueness forces interviewers to infer meaning. In a structured interview, inference usually results in lower marks.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Specificity Creates Credibility
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Precise answers answer the unspoken questions:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400 text-center">
            <div class="text-2xl mb-2">❓</div>
            <h5 class="font-semibold text-blue-800 mb-2">What exactly</h5>
            <p class="text-blue-700 text-sm">did you do?</p>
          </div>
          <div class="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400 text-center">
            <div class="text-2xl mb-2">📍</div>
            <h5 class="font-semibold text-blue-800 mb-2">In what context</h5>
            <p class="text-blue-700 text-sm">did it happen?</p>
          </div>
          <div class="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400 text-center">
            <div class="text-2xl mb-2">📊</div>
            <h5 class="font-semibold text-blue-800 mb-2">With what outcome</h5>
            <p class="text-blue-700 text-sm">did it end?</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Using concrete details — such as settings, roles, or outcomes — anchors your answer in reality and makes your experience credible.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          The Role of the 5W + H Framework
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">A useful way to avoid vagueness is to briefly consider:</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <div class="grid md:grid-cols-3 gap-3 mb-3">
            <div class="text-center p-2 bg-white rounded">
              <span class="text-green-700 font-medium">What happened?</span>
            </div>
            <div class="text-center p-2 bg-white rounded">
              <span class="text-green-700 font-medium">Who was involved?</span>
            </div>
            <div class="text-center p-2 bg-white rounded">
              <span class="text-green-700 font-medium">Where & when?</span>
            </div>
            <div class="text-center p-2 bg-white rounded">
              <span class="text-green-700 font-medium">Why important?</span>
            </div>
            <div class="text-center p-2 bg-white rounded">
              <span class="text-green-700 font-medium">How did you act?</span>
            </div>
            <div class="text-center p-2 bg-white rounded">
              <span class="text-green-700 font-medium">What was the outcome?</span>
            </div>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">You do not need to answer all of these explicitly, but they help ensure your answer has substance.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          When Detail Becomes a Problem
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Too much detail can be just as harmful as too little. Overly detailed answers often:</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <ul class="space-y-2 text-orange-700">
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🎯</span>
              <span>Lose the main message</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">⏰</span>
              <span>Exceed time limits</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🔍</span>
              <span>Obscure key competencies</span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">⚠️ <strong>Interviewers may remember the detail but miss the point — which affects scoring.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Choosing What to Leave Out
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Strong candidates are selective. They include:</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-4 border-l-4 border-teal-400">
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">✅</span>
              <span>Information that directly supports the question</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📈</span>
              <span>Details that demonstrate judgement or learning</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">They exclude:</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-8 border-l-4 border-pink-400">
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">❌</span>
              <span>Technical minutiae</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">📖</span>
              <span>Tangential anecdotes</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🔍</span>
              <span>Irrelevant background</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This selectivity reflects maturity and situational awareness.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Precision Signals Professional Judgement
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">In clinical practice, doctors constantly filter information:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 text-center">
            <div class="text-2xl mb-2">📋</div>
            <h5 class="font-semibold text-purple-800 mb-2">During handover</h5>
            <p class="text-purple-700 text-sm">Prioritizing key information</p>
          </div>
          <div class="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 text-center">
            <div class="text-2xl mb-2">📝</div>
            <h5 class="font-semibold text-purple-800 mb-2">In documentation</h5>
            <p class="text-purple-700 text-sm">Focusing on essentials</p>
          </div>
          <div class="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 text-center">
            <div class="text-2xl mb-2">💬</div>
            <h5 class="font-semibold text-purple-800 mb-2">When explaining plans</h5>
            <p class="text-purple-700 text-sm">Communicating clearly</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Interviewers recognise precision as a transferable professional skill.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🎯</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Precision is not about saying less. It is about saying only what matters.</p>
          <p class="text-indigo-100 font-semibold">By avoiding vagueness and unnecessary detail: Your answers become clearer, Your competence becomes easier to recognise, Your scores become more consistent.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">🎯</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 18,
      slug: 'talking-about-yourself-without-arrogant',
      category: 'Interview Insights',
      title: 'Talking About Yourself Without Sounding Arrogant',
      excerpt: 'For many candidates, the hardest part of an interview is not answering clinical questions, but talking about themselves. Discomfort often leads to vague language.',
      author: 'Medical Interview Team',
      date: 'Feb 03, 2025',
      readTime: '4 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border-l-4 border-amber-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🙋‍♂️</span>
            </div>
            <h3 class="text-lg font-bold text-amber-800">The Challenge of Self-Description</h3>
          </div>
          <p class="text-slate-700 italic">For many candidates, the hardest part of an interview is not answering clinical questions, but talking about themselves. Discomfort often leads to vague language.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Interviewers Need to Hear "I"
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviews assess individual performance, not team dynamics alone. Although teamwork is highly valued, interviewers must understand:</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">👤</span>
              <span><strong>What you personally did</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🎯</span>
              <span><strong>What decisions you made</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">⚖️</span>
              <span><strong>What responsibility you held</strong></span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">❌ <strong>When answers focus entirely on "we," interviewers are left guessing. Guessing rarely leads to high scores.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          The "We" Trap
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Many candidates rely heavily on collective language to appear collaborative. However, overuse of "we" can obscure:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400 text-center">
            <div class="text-2xl mb-2">🚀</div>
            <h5 class="font-semibold text-blue-800 mb-2">Initiative</h5>
            <p class="text-blue-700 text-sm">Individual action</p>
          </div>
          <div class="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400 text-center">
            <div class="text-2xl mb-2">👑</div>
            <h5 class="font-semibold text-blue-800 mb-2">Leadership</h5>
            <p class="text-blue-700 text-sm">Personal influence</p>
          </div>
          <div class="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400 text-center">
            <div class="text-2xl mb-2">🎯</div>
            <h5 class="font-semibold text-blue-800 mb-2">Accountability</h5>
            <p class="text-blue-700 text-sm">Personal responsibility</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">A balanced answer might begin with team context, but should quickly clarify your role within that team.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Ownership Without Ego
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Talking about yourself does not require self-praise. It requires clarity.</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <p class="text-green-800 mb-3">Effective self-focus includes:</p>
          <ul class="space-y-2 text-green-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📋</span>
              <span>Describing your responsibilities</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🎯</span>
              <span>Explaining your decision-making</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🔄</span>
              <span>Reflecting on your learning</span>
            </li>
          </ul>
        </div>

        <div class="bg-indigo-50 p-4 rounded-lg mb-8 border-l-4 border-indigo-400">
          <p class="text-indigo-800 font-medium">🤝 <strong>Acknowledging others' contributions while clearly outlining your own role signals professionalism, not arrogance.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Using Objectivity to Stay Comfortable
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">If self-description feels awkward, objectivity can help. Referring to:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-400 text-center">
            <div class="text-2xl mb-2">🤒</div>
            <h5 class="font-semibold text-teal-800 mb-2">Feedback from patients</h5>
            <p class="text-teal-700 text-sm">External validation</p>
          </div>
          <div class="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-400 text-center">
            <div class="text-2xl mb-2">👨‍⚕️</div>
            <h5 class="font-semibold text-teal-800 mb-2">Comments from colleagues</h5>
            <p class="text-teal-700 text-sm">Peer recognition</p>
          </div>
          <div class="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-400 text-center">
            <div class="text-2xl mb-2">📊</div>
            <h5 class="font-semibold text-teal-800 mb-2">Formal appraisals</h5>
            <p class="text-teal-700 text-sm">Structured assessment</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">allows you to describe strengths without appearing self-congratulatory. This shifts the focus from opinion to evidence.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Why Avoiding Self-Focus Hurts Your Score
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">When interviewers cannot identify your contribution, they cannot map your answer to positive indicators. This often results in:</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">📉</span>
              <span>Lower scores</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">❌</span>
              <span>Missed opportunities</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">👎</span>
              <span>Underestimation of ability</span>
            </li>
          </ul>
        </div>

        <div class="bg-emerald-50 p-4 rounded-lg mb-8 border-l-4 border-emerald-400">
          <p class="text-emerald-800 font-medium">✅ <strong>Clear self-focus supports fair assessment.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Self-Awareness Is What Interviewers Are Really Listening For
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers are not looking for perfection. They are listening for self-awareness.</p>

        <div class="bg-purple-50 p-4 rounded-lg mb-6 border-l-4 border-purple-400">
          <p class="text-purple-800 mb-3">Candidates who speak openly about:</p>
          <ul class="space-y-2 text-purple-700">
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">✅</span>
              <span>What they did well</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">📈</span>
              <span>What they are still developing</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">🔄</span>
              <span>How they respond to feedback</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">are often perceived as safer and more mature than those who avoid personal evaluation.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🙋‍♂️</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Interviews are not the place to disappear into the team. They are the place to demonstrate your individual professional identity.</p>
          <p class="text-indigo-100 font-semibold">By talking about yourself clearly, objectively, and respectfully: Your contributions become visible, Your strengths are easier to score, Your professionalism comes through naturally.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">🙋‍♂️</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 17,
      slug: 'language-signals-confidence-power-words',
      category: 'Interview Insights',
      title: 'Language That Signals Confidence: Power Words, Active Verbs, and Ownership',
      excerpt: 'Confidence in interviews is communicated less by what you say than by how you say it. Subtle choices in language shape how interviewers perceive your competence.',
      author: 'Medical Interview Team',
      date: 'Feb 01, 2025',
      readTime: '5 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border-l-4 border-rose-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-rose-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">💬</span>
            </div>
            <h3 class="text-lg font-bold text-rose-800">How You Say It Matters</h3>
          </div>
          <p class="text-slate-700 italic">Confidence in interviews is communicated less by what you say than by how you say it. Subtle choices in language shape how interviewers perceive your competence.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Language Matters More Than Candidates Realise
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers listen not only for content, but for agency. They pay attention to whether you present yourself as someone who acts, decides, and reflects — or someone to whom things merely happen.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 mb-3 font-medium">Passive language often unintentionally suggests:</p>
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🤔</span>
              <span><strong>Hesitation</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🙋‍♂️</span>
              <span><strong>Lack of ownership</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">😟</span>
              <span><strong>Limited confidence</strong></span>
            </li>
          </ul>
        </div>

        <div class="bg-blue-50 p-4 rounded-lg mb-8 border-l-4 border-blue-400">
          <p class="text-blue-800 font-medium">💪 <strong>Active language suggests the opposite.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          The Difference Between Passive and Active Ownership
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Consider the difference between:</p>

        <div class="bg-red-50 p-4 rounded-lg mb-4 border-l-4 border-red-400">
          <h5 class="font-semibold text-red-800 mb-2">Passive:</h5>
          <p class="text-red-700 italic">"I was able to help resolve the issue."</p>
        </div>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <h5 class="font-semibold text-green-800 mb-2">Active:</h5>
          <p class="text-green-700 italic">"I led discussions that resolved the issue."</p>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Both may describe the same event, but they create very different impressions. Active phrasing signals initiative and responsibility without exaggeration.</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-8 border-l-4 border-indigo-400">
          <p class="text-indigo-800 font-medium">🎯 <strong>Ownership does not mean claiming sole credit. It means clearly describing your contribution.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Power Words Are About Precision, Not Exaggeration
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Power words are not about sounding dramatic or boastful. They are about choosing verbs that accurately reflect action and intent.</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <p class="text-orange-800 mb-3">Words such as:</p>
          <div class="grid md:grid-cols-3 gap-3 mb-3">
            <div class="bg-white p-2 rounded text-center">
              <span class="text-orange-700 font-medium">coordinated</span>
            </div>
            <div class="bg-white p-2 rounded text-center">
              <span class="text-orange-700 font-medium">initiated</span>
            </div>
            <div class="bg-white p-2 rounded text-center">
              <span class="text-orange-700 font-medium">supported</span>
            </div>
            <div class="bg-white p-2 rounded text-center">
              <span class="text-orange-700 font-medium">facilitated</span>
            </div>
            <div class="bg-white p-2 rounded text-center">
              <span class="text-orange-700 font-medium">implemented</span>
            </div>
          </div>
          <p class="text-orange-800">provide clarity about your role.</p>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Vague phrases such as "was involved in" or "had the opportunity to" obscure it.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Why Weak Language Undermines Strong Experience
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Candidates often soften their language out of modesty or discomfort talking about themselves. Unfortunately, this can make genuine achievements sound accidental or minor.</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 mb-3">Interviewers do not reward understatement. They reward clear, proportionate ownership.</p>
        </div>

        <div class="bg-emerald-50 p-4 rounded-lg mb-8 border-l-4 border-emerald-400">
          <p class="text-emerald-800 mb-3">Using precise language helps interviewers:</p>
          <ul class="space-y-2 text-emerald-700">
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">👀</span>
              <span>Understand your responsibilities</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">👑</span>
              <span>Identify leadership and initiative</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">📊</span>
              <span>Score you fairly</span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Sounding Confident Without Sounding Arrogant
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Confidence is not about claiming superiority. It is about being comfortable describing your role honestly.</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <p class="text-pink-800 mb-3">Balanced language:</p>
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🤝</span>
              <span>Acknowledges teamwork</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🎯</span>
              <span>Highlights individual responsibility</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🙅‍♂️</span>
              <span>Avoids self-promotion</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">For example, describing how you contributed to a team outcome while recognising others' roles demonstrates maturity rather than ego.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Language Reflects Professional Identity
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The way you speak about your work reveals how you see yourself as a professional. Active, clear language suggests:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 text-center">
            <div class="text-2xl mb-2">🎯</div>
            <h5 class="font-semibold text-purple-800 mb-2">Readiness for</h5>
            <p class="text-purple-700 text-sm">responsibility</p>
          </div>
          <div class="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 text-center">
            <div class="text-2xl mb-2">⚖️</div>
            <h5 class="font-semibold text-purple-800 mb-2">Comfort with</h5>
            <p class="text-purple-700 text-sm">accountability</p>
          </div>
          <div class="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 text-center">
            <div class="text-2xl mb-2">🔍</div>
            <h5 class="font-semibold text-purple-800 mb-2">Self-awareness</h5>
            <p class="text-purple-700 text-sm">of your role</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">These traits are highly valued in structured interviews.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">💬</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Strong candidates do not inflate their experience. They articulate it clearly.</p>
          <p class="text-indigo-100 font-semibold">By using active verbs and owning your contributions: Your answers sound more confident, Your competence becomes clearer, Interviewers find it easier to score you highly. Language does not change your experience — but it changes how that experience is understood.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">💬</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 16,
      slug: 'signposting-your-thinking-message',
      category: 'Interview Insights',
      title: 'Signposting Your Thinking: Making Your Message Impossible to Miss',
      excerpt: 'Many interview answers contain strong content but still fail to land. The issue is rarely what is said — it is how clearly the message is signposted.',
      author: 'Medical Interview Team',
      date: 'Jan 30, 2025',
      readTime: '5 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl border-l-4 border-cyan-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🗺️</span>
            </div>
            <h3 class="text-lg font-bold text-cyan-800">Mental Roadmap for Your Answers</h3>
          </div>
          <p class="text-slate-700 italic">Many interview answers contain strong content but still fail to land. The issue is rarely what is said — it is how clearly the message is signposted.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          What Signposting Actually Means
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Signposting is the act of clearly announcing your key points so the listener knows what to listen for. It provides a mental roadmap for your answer.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 mb-3 font-medium">Without signposting, even a well-structured response can feel vague or unfocused. With signposting, interviewers immediately understand:</p>
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">💬</span>
              <span><strong>What your main messages are</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📋</span>
              <span><strong>How your answer is organised</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📊</span>
              <span><strong>Where to allocate marks</strong></span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Why Signposting Improves Scoring
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers must identify positive indicators quickly. Signposting makes those indicators visible.</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <p class="text-blue-800 mb-3">For example, when you clearly state:</p>
          <div class="space-y-2">
            <div class="bg-white p-3 rounded-lg border-l-4 border-blue-300 ml-4">
              <p class="text-blue-700 italic">"One key strength is..."</p>
            </div>
            <div class="bg-white p-3 rounded-lg border-l-4 border-blue-300 ml-4">
              <p class="text-blue-700 italic">"A second important lesson I learned was..."</p>
            </div>
            <div class="bg-white p-3 rounded-lg border-l-4 border-blue-300 ml-4">
              <p class="text-blue-700 italic">"The main reason I am drawn to this specialty is..."</p>
            </div>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">you are actively helping interviewers locate the behaviours they are scoring.</p>

        <div class="bg-green-50 p-4 rounded-lg mb-8 border-l-4 border-green-400">
          <p class="text-green-800 font-medium">✅ <strong>This clarity reduces ambiguity and improves confidence in marking.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Signposting at the Start vs the End
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">There is more than one way to signpost effectively.</p>

        <div class="grid md:grid-cols-2 gap-6 mb-8">
          <div class="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
            <h5 class="font-semibold text-orange-800 mb-3">Signposting at the Start:</h5>
            <p class="text-orange-700 text-sm mb-2">Stating your key message before expanding on it</p>
            <p class="text-orange-700 text-sm italic">Works well when you want your main point to be immediately clear</p>
          </div>
          <div class="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-400">
            <h5 class="font-semibold text-teal-800 mb-3">Signposting at the End:</h5>
            <p class="text-teal-700 text-sm mb-2">Describing your experience first and then explicitly linking it back to the question</p>
            <p class="text-teal-700 text-sm italic">Can feel more natural and reflective</p>
          </div>
        </div>

        <div class="bg-indigo-50 p-4 rounded-lg mb-8 border-l-4 border-indigo-400">
          <p class="text-indigo-800 font-medium">🎯 <strong>Both approaches are valid. What matters is that the message is unmistakable.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Avoiding the "Over-Mechanical" Trap
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Signposting should feel supportive, not robotic. Repeating the same phrasing at the start of every point can sound overly rigid.</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <p class="text-pink-800 mb-3">As confidence grows, varying where and how you signpost:</p>
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🌊</span>
              <span>Improves flow</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">💬</span>
              <span>Sounds more conversational</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">👂</span>
              <span>Keeps the panel engaged</span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">⚠️ <strong>However, clarity should always take priority over style.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          When Signposting Matters Most
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Signposting is particularly important when:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400 text-center">
            <div class="text-2xl mb-2">❓</div>
            <h5 class="font-semibold text-emerald-800 mb-2">Answering broad</h5>
            <p class="text-emerald-700 text-sm">or open-ended questions</p>
          </div>
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400 text-center">
            <div class="text-2xl mb-2">🧩</div>
            <h5 class="font-semibold text-emerald-800 mb-2">Discussing complex</h5>
            <p class="text-emerald-700 text-sm">experiences</p>
          </div>
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400 text-center">
            <div class="text-2xl mb-2">🔄</div>
            <h5 class="font-semibold text-emerald-800 mb-2">Reflecting on learning</h5>
            <p class="text-emerald-700 text-sm">or motivation</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">In these situations, interviewers are listening for meaning rather than facts. Clear signposting ensures your intended message is not lost.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Signposting Is a Professional Skill
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Clear communication is a clinical skill. In practice, doctors signpost constantly:</p>

        <div class="bg-cyan-50 p-4 rounded-lg mb-6 border-l-4 border-cyan-400">
          <ul class="space-y-2 text-cyan-700">
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">🤝</span>
              <span>When explaining plans to patients</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">👥</span>
              <span>When handing over to colleagues</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">👨‍⚕️</span>
              <span>When leading teams</span>
            </li>
          </ul>
        </div>

        <div class="bg-purple-50 p-4 rounded-lg mb-8 border-l-4 border-purple-400">
          <p class="text-purple-800 font-medium">🎯 <strong>Interviewers recognise this. Effective signposting signals maturity and readiness for responsibility.</strong></p>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🗺️</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Good answers contain strong content. Great answers make that content easy to find.</p>
          <p class="text-indigo-100 font-semibold">Signposting reduces misunderstanding, highlights your strengths, and makes scoring fairer. If interviewers can easily identify your key messages, they are far more likely to recognise — and reward — your competence.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">🗺️</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 15,
      slug: 'buzzwords-to-evidence-examples',
      category: 'Interview Insights',
      title: 'From Buzzwords to Evidence: Why Examples Separate Average from Excellent',
      excerpt: 'Most candidates know the right words to use in an interview. They describe themselves as motivated, hardworking, reflective, and good communicators.',
      author: 'Medical Interview Team',
      date: 'Jan 28, 2025',
      readTime: '5 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl border-l-4 border-purple-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">💡</span>
            </div>
            <h3 class="text-lg font-bold text-purple-800">Demonstrated vs Declared</h3>
          </div>
          <p class="text-slate-700 italic">Most candidates know the right words to use in an interview. They describe themselves as motivated, hardworking, reflective, and good communicators.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Buzzwords Fail to Impress
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Buzzwords are attractive because they feel safe. They sound professional and familiar, and they appear to cover a wide range of competencies in a single sentence.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 mb-3 font-medium">The problem is that:</p>
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">👥</span>
              <span><strong>Everyone uses them</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🗣️</span>
              <span><strong>They are easy to say</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📊</span>
              <span><strong>They are difficult to score</strong></span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">❌ <strong>When an answer consists only of general descriptors, interviewers have no concrete behaviour to evaluate. As a result, scores remain average.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Evidence Turns Claims Into Proof
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers are trained to look for what you did, not what you believe about yourself. Examples transform abstract qualities into observable actions.</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <p class="text-blue-800 mb-3">For instance:</p>
          <div class="grid md:grid-cols-2 gap-4">
            <div class="bg-red-50 p-3 rounded-lg border-l-4 border-red-400">
              <h5 class="font-semibold text-red-800 mb-2">Claim (Buzzword):</h5>
              <p class="text-red-700 text-sm">"I learn from experience"</p>
            </div>
            <div class="bg-green-50 p-3 rounded-lg border-l-4 border-green-400">
              <h5 class="font-semibold text-green-800 mb-2">Evidence (Example):</h5>
              <p class="text-green-700 text-sm">"A challenging interaction with a patient taught me to listen more carefully before responding"</p>
            </div>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Evidence allows interviewers to assess:</p>

        <div class="grid md:grid-cols-4 gap-3 mb-8">
          <div class="bg-slate-50 p-3 rounded-lg border-l-4 border-slate-400 text-center">
            <div class="text-xl mb-1">🔍</div>
            <p class="text-slate-700 font-medium text-sm">Insight</p>
          </div>
          <div class="bg-slate-50 p-3 rounded-lg border-l-4 border-slate-400 text-center">
            <div class="text-xl mb-1">🎯</div>
            <p class="text-slate-700 font-medium text-sm">Responsibility</p>
          </div>
          <div class="bg-slate-50 p-3 rounded-lg border-l-4 border-slate-400 text-center">
            <div class="text-xl mb-1">🔄</div>
            <p class="text-slate-700 font-medium text-sm">Reflection</p>
          </div>
          <div class="bg-slate-50 p-3 rounded-lg border-l-4 border-slate-400 text-center">
            <div class="text-xl mb-1">💪</div>
            <p class="text-slate-700 font-medium text-sm">Impact</p>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Why Examples Signal Professional Maturity
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Candidates who use examples naturally demonstrate several competencies at once. A single well-chosen example can show:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400 text-center">
            <div class="text-2xl mb-2">🩺</div>
            <h5 class="font-semibold text-green-800 mb-2">Clinical judgement</h5>
            <p class="text-green-700 text-sm">in action</p>
          </div>
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400 text-center">
            <div class="text-2xl mb-2">💬</div>
            <h5 class="font-semibold text-green-800 mb-2">Communication skills</h5>
            <p class="text-green-700 text-sm">demonstrated</p>
          </div>
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400 text-center">
            <div class="text-2xl mb-2">🤝</div>
            <h5 class="font-semibold text-green-800 mb-2">Teamwork</h5>
            <p class="text-green-700 text-sm">and leadership</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This efficiency is a hallmark of strong candidates. It shows that experience has been processed and integrated, not just accumulated.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Choosing the Right Level of Detail
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Examples do not need to be long or dramatic. In fact, overly detailed stories often distract from the core message.</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 mb-3">Effective examples are:</p>
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🎯</span>
              <span>Specific enough to be credible</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">⏱️</span>
              <span>Brief enough to stay focused</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📋</span>
              <span>Directly relevant to the question</span>
            </li>
          </ul>
        </div>

        <div class="bg-cyan-50 p-4 rounded-lg mb-8 border-l-4 border-cyan-400">
          <p class="text-cyan-800 font-medium">🎯 <strong>The purpose of an example is to illustrate a point, not to tell a full story.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Reflection Is What Elevates an Example
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">An example without reflection remains descriptive. Reflection is what turns experience into learning.</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <p class="text-pink-800 mb-3">High-scoring answers explain:</p>
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🔄</span>
              <span>What changed as a result of the experience</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">📈</span>
              <span>How future behaviour was influenced</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">💡</span>
              <span>Why the learning matters</span>
            </li>
          </ul>
        </div>

        <div class="bg-indigo-50 p-4 rounded-lg mb-8 border-l-4 border-indigo-400">
          <p class="text-indigo-800 font-medium">🌱 <strong>This signals growth — a key attribute interviewers value.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Why Examples Move You from "Satisfactory" to "Excellent"
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">At a basic level, examples confirm that you have done the work. At a higher level, they show how you think about that work.</p>

        <div class="bg-emerald-50 p-4 rounded-lg mb-6 border-l-4 border-emerald-400">
          <p class="text-emerald-800 mb-3">Candidates who score well consistently:</p>
          <ul class="space-y-2 text-emerald-700">
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">🎯</span>
              <span>Make clear claims</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">💡</span>
              <span>Support them with relevant examples</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">🔄</span>
              <span>Reflect on outcomes and learning</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This combination is what differentiates an acceptable answer from a compelling one.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">💡</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Buzzwords make answers sound professional. Examples make answers credible.</p>
          <p class="text-indigo-100 font-semibold">If you want to stand out: Replace lists of qualities with lived experience, Use examples to demonstrate not decorate, Show learning not just action. That is where higher scores come from.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">💡</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 14,
      slug: 'structure-not-style-frameworks',
      category: 'Interview Insights',
      title: 'Structure Is Not Style: How Clear Frameworks Help Interviewers Score You',
      excerpt: 'Many candidates believe that structure is about sounding polished. In reality, structure is about being understood and scorable.',
      author: 'Medical Interview Team',
      date: 'Jan 25, 2025',
      readTime: '5 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl border-l-4 border-emerald-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">📋</span>
            </div>
            <h3 class="text-lg font-bold text-emerald-800">Making Competence Visible</h3>
          </div>
          <p class="text-slate-700 italic">Many candidates believe that structure is about sounding polished. In reality, structure is about being understood and scorable.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Interviewers Depend on Structure
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers must listen, evaluate, and score at the same time. When an answer lacks structure, they are forced to work harder to identify what the candidate is trying to say — and which competencies are being demonstrated.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 mb-3 font-medium">Unstructured answers create uncertainty.</p>
          <p class="text-slate-700">Uncertainty leads to conservative scoring.</p>
        </div>

        <div class="bg-blue-50 p-4 rounded-lg mb-8 border-l-4 border-blue-400">
          <p class="text-blue-800 font-medium">💡 <strong>Structure, therefore, is not a stylistic preference. It is a scoring aid.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          The Cognitive Limit: Why Three or Four Points Work Best
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Human working memory is limited. Interviewers, like all listeners, can comfortably retain three or four ideas at once. Answers that contain too many points blur together and lose impact.</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-6 border-l-4 border-indigo-400">
          <p class="text-indigo-800 mb-3">Limiting your answer to three or four clear points:</p>
          <ul class="space-y-2 text-indigo-700">
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">🧠</span>
              <span>Improves comprehension</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">🎯</span>
              <span>Highlights priorities</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">✅</span>
              <span>Makes positive indicators easier to spot</span>
            </li>
          </ul>
        </div>

        <div class="bg-orange-50 border-l-4 border-orange-400 p-4 mb-8 rounded-r-lg">
          <p class="text-orange-800 font-medium">⚠️ <strong>Trying to cover everything often results in covering nothing well.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Structure Reveals Judgement
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">How you organise your answer reflects how you think. A clear structure demonstrates:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400 text-center">
            <div class="text-2xl mb-2">📋</div>
            <h5 class="font-semibold text-green-800 mb-2">Ability to prioritise</h5>
            <p class="text-green-700 text-sm">information</p>
          </div>
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400 text-center">
            <div class="text-2xl mb-2">🔄</div>
            <h5 class="font-semibold text-green-800 mb-2">Logical sequencing</h5>
            <p class="text-green-700 text-sm">of ideas</p>
          </div>
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400 text-center">
            <div class="text-2xl mb-2">🎯</div>
            <h5 class="font-semibold text-green-800 mb-2">Awareness of relevance</h5>
            <p class="text-green-700 text-sm">to the question</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">These are the same skills required in clinical reasoning, teaching, and leadership. Interviewers interpret structure as a sign of professional maturity.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Simple Structures That Work Consistently
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Strong candidates use simple, repeatable frameworks — not rigid scripts.</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 mb-3">For example:</p>
          <div class="space-y-3">
            <div class="bg-white p-3 rounded-lg">
              <h5 class="font-semibold text-teal-800">When describing experience:</h5>
              <p class="text-teal-700 text-sm">context → action → outcome → learning</p>
            </div>
            <div class="bg-white p-3 rounded-lg">
              <h5 class="font-semibold text-teal-800">When discussing mistakes:</h5>
              <p class="text-teal-700 text-sm">what happened → response → reflection → change</p>
            </div>
            <div class="bg-white p-3 rounded-lg">
              <h5 class="font-semibold text-teal-800">When outlining strengths:</h5>
              <p class="text-teal-700 text-sm">trait → example → impact</p>
            </div>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">These structures allow interviewers to clearly map your answer to assessment criteria.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Why Over-Structuring Can Also Be a Problem
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Structure should guide, not constrain. Answers that feel overly mechanical or rehearsed can sound unnatural and reduce authenticity.</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <p class="text-pink-800 mb-3">The goal is not to follow a template perfectly, but to ensure that:</p>
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🎯</span>
              <span>Each point is distinct</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">📈</span>
              <span>Each point is developed</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">💬</span>
              <span>The overall message is clear</span>
            </li>
          </ul>
        </div>

        <div class="bg-cyan-50 p-4 rounded-lg mb-8 border-l-4 border-cyan-400">
          <p class="text-cyan-800 font-medium">🎯 <strong>Structure should feel supportive, not rigid.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          How Structure Improves Confidence
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Candidates often feel more confident when they know where their answer is going. Structure reduces hesitation, rambling, and filler phrases.</p>

        <div class="bg-emerald-50 p-4 rounded-lg mb-6 border-l-4 border-emerald-400">
          <p class="text-emerald-800 mb-3">This confidence is perceived positively by interviewers and contributes to:</p>
          <ul class="space-y-2 text-emerald-700">
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">💬</span>
              <span>Clear communication</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">⏱️</span>
              <span>Controlled pacing</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">👔</span>
              <span>Professional presence</span>
            </li>
          </ul>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">📋</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Structure is not about style. It is about making your competence visible.</p>
          <p class="text-indigo-100 font-semibold">When your answers are organised into three or four clear points: Interviewers understand you more easily, Positive indicators are clearer, Scoring becomes more accurate. In a structured interview, structure works in your favour.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">📋</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 13,
      slug: 'answer-question-asked-relevance',
      category: 'Interview Insights',
      title: 'Answer the Question You Were Asked: Relevance Over Knowledge',
      excerpt: 'One of the most common reasons strong candidates underperform at interview is not lack of knowledge, but failure to answer the question directly.',
      author: 'Medical Interview Team',
      date: 'Jan 22, 2025',
      readTime: '5 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl border-l-4 border-cyan-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🎯</span>
            </div>
            <h3 class="text-lg font-bold text-cyan-800">The Art of Staying on Target</h3>
          </div>
          <p class="text-slate-700 italic">One of the most common reasons strong candidates underperform at interview is not lack of knowledge, but failure to answer the question directly.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Interviewers Notice Irrelevant Answers Immediately
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interview questions are carefully designed to test specific competencies. When a candidate answers a different question — even a closely related one — interviewers struggle to identify the behaviours they are meant to score.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 mb-3 font-medium">This creates two problems:</p>
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🔍</span>
              <span><strong>Positive indicators become harder to detect</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">⚖️</span>
              <span><strong>Interviewers are forced to interpret rather than assess</strong></span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">⚠️ <strong>When that happens, scores tend to drop.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Definitions Are a Common Trap
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">A classic mistake is responding to experience-based questions with definitions or theory.</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <p class="text-blue-800 mb-3">For example, when asked about experience in areas such as governance, communication, or leadership, candidates often begin by explaining what these concepts mean in general terms. While accurate, this approach does not address the question.</p>
        </div>

        <div class="grid md:grid-cols-2 gap-4 mb-8">
          <div class="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
            <h5 class="font-semibold text-red-800 mb-2">Interviewers are NOT asking:</h5>
            <ul class="text-sm text-red-700 space-y-1">
              <li>• What is clinical governance?</li>
              <li>• Why is communication important?</li>
            </ul>
          </div>
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
            <h5 class="font-semibold text-green-800 mb-2">They ARE asking:</h5>
            <ul class="text-sm text-green-700 space-y-1">
              <li>• What have you done?</li>
              <li>• How have you applied this in practice?</li>
              <li>• What did you learn?</li>
            </ul>
          </div>
        </div>

        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-r-lg">
          <p class="text-yellow-800 font-medium">⚠️ <strong>Starting with a definition delays the answer and wastes a valuable opportunity to demonstrate real-world engagement.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Knowledge Without Context Scores Poorly
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Demonstrating knowledge is expected. Demonstrating applied knowledge is what scores.</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <p class="text-orange-800 mb-3">Answers that focus heavily on:</p>
          <ul class="space-y-2 text-orange-700">
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">📋</span>
              <span>Frameworks</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">📖</span>
              <span>Guidelines</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🧠</span>
              <span>Abstract principles</span>
            </li>
          </ul>
          <p class="text-orange-800 mt-3">without grounding them in personal experience often sound detached and impersonal.</p>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Interviewers struggle to assess judgement, insight, or responsibility when answers remain theoretical.</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-8 border-l-4 border-indigo-400">
          <p class="text-indigo-800 font-medium">💡 <strong>Knowledge becomes meaningful only when it is connected to:</strong></p>
          <ul class="text-indigo-700 text-sm space-y-1 mt-2">
            <li>• Decisions you made</li>
            <li>• Actions you took</li>
            <li>• Outcomes you observed</li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Why Candidates Drift Off-Question
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Most off-target answers happen for predictable reasons:</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">⏰</span>
              <span>Candidates are trying to buy time</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">😰</span>
              <span>They are nervous and default to rehearsed material</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🎭</span>
              <span>They want to appear knowledgeable rather than reflective</span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">❌ <strong>Unfortunately, none of these help the interviewer score you well.</strong></p>
        </div>

        <div class="bg-green-50 p-4 rounded-lg mb-8 border-l-4 border-green-400">
          <p class="text-green-800 font-medium">✅ <strong>A direct answer signals confidence. An indirect answer signals uncertainty — even if unintended.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          What "Answering Directly" Actually Looks Like
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Answering the question directly does not mean being abrupt or simplistic. It means starting where the interviewer expects you to start.</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <p class="text-pink-800 mb-3">Strong answers typically:</p>
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">👂</span>
              <span>Acknowledge the question explicitly</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🎯</span>
              <span>State your position or experience early</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">📈</span>
              <span>Then develop it with examples and reflection</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This allows interviewers to immediately understand:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400 text-center">
            <div class="text-2xl mb-2">❓</div>
            <p class="text-slate-700 font-medium">What your answer is about</p>
          </div>
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400 text-center">
            <div class="text-2xl mb-2">🎯</div>
            <p class="text-slate-700 font-medium">Which competency is being addressed</p>
          </div>
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400 text-center">
            <div class="text-2xl mb-2">📊</div>
            <p class="text-slate-700 font-medium">Where to focus their scoring</p>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Relevance Makes You Easier to Score — and That Matters
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers are working under time pressure. They must listen, evaluate, and score simultaneously. Answers that stay tightly aligned with the question make this task easier.</p>

        <div class="bg-emerald-50 p-4 rounded-lg mb-6 border-l-4 border-emerald-400">
          <p class="text-emerald-800 mb-3">When your answer is relevant:</p>
          <ul class="space-y-2 text-emerald-700">
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">🔍</span>
              <span>Key behaviours are clear</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">✅</span>
              <span>Positive indicators stand out</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">💪</span>
              <span>Your competence feels more convincing</span>
            </li>
          </ul>
        </div>

        <div class="bg-cyan-50 p-4 rounded-lg mb-8 border-l-4 border-cyan-400">
          <p class="text-cyan-800 font-medium">🎯 <strong>This is not about "gaming" the system. It is about respecting the structure of the assessment.</strong></p>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🎯</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Medical interviews do not reward the most knowledgeable speaker. They reward the most relevant one.</p>
          <p class="text-indigo-100 font-semibold">If you answer the question you were actually asked, focus on your own experience, and apply knowledge rather than display it, you make it easier for interviewers to recognise your strengths — and to score you accordingly.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">🎯</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 12,
      slug: 'why-time-matters-answer-length',
      category: 'Interview Insights',
      title: 'Why Time Matters: How Answer Length Influences Interview Scores',
      excerpt: 'Medical interviews are not won by saying more. They are won by saying enough — clearly, confidently, and within the limits of human attention.',
      author: 'Medical Interview Team',
      date: 'Jan 20, 2025',
      readTime: '6 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl border-l-4 border-orange-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">⏱️</span>
            </div>
            <h3 class="text-lg font-bold text-orange-800">The Hidden Cost of Speaking Too Long</h3>
          </div>
          <p class="text-slate-700 italic">Medical interviews are not won by saying more. They are won by saying enough — clearly, confidently, and within the limits of human attention.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Interviews Are a Communication Exercise First
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">At their core, interviews are not examinations of memory. They are structured communication exercises. Interviewers are assessing how effectively you can convey relevant information under time pressure — a skill that mirrors real clinical practice.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 mb-3 font-medium">The paradox is clear:</p>
          <div class="grid md:grid-cols-2 gap-4">
            <div class="bg-red-50 p-3 rounded-lg border-l-4 border-red-400">
              <h5 class="font-semibold text-red-800 mb-2">Too Long →</h5>
              <p class="text-red-700 text-sm">Suggests difficulty with prioritisation</p>
            </div>
            <div class="bg-red-50 p-3 rounded-lg border-l-4 border-red-400">
              <h5 class="font-semibold text-red-800 mb-2">Too Short →</h5>
              <p class="text-red-700 text-sm">Suggests lack of depth or insight</p>
            </div>
          </div>
          <p class="text-slate-700 mt-3 text-sm">Neither scores well.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          The 90–120 Second Rule: Not Arbitrary, but Cognitive
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Most interview formats implicitly allow around 90 seconds to 2 minutes per answer. This is not accidental. Human attention declines rapidly beyond this point unless the speaker is exceptional or supported by visual aids — neither of which applies in an interview room.</p>

        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-r-lg">
          <p class="text-yellow-800 font-medium">⚠️ <strong>Once attention drops:</strong></p>
          <ul class="text-yellow-700 text-sm space-y-1 mt-2">
            <li>• Key points are missed</li>
            <li>• Structure becomes unclear</li>
            <li>• Positive indicators are harder to identify</li>
            <li>• Scoring becomes conservative</li>
          </ul>
          <p class="text-yellow-800 text-sm mt-2 font-medium">In other words, you may still be talking, but you are no longer being heard.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          When Longer Answers Are Acceptable — and When They Are Not
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">There are limited exceptions where slightly longer answers are reasonable:</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <ul class="space-y-2 text-green-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">👤</span>
              <span><strong>"Tell us about yourself"</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📄</span>
              <span><strong>"Talk us through your CV"</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🔬</span>
              <span><strong>"Describe your research experience"</strong> (if substantial)</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Even then, answers should rarely exceed three minutes. Length beyond this usually reflects poor filtering rather than rich experience.</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-8 border-l-4 border-indigo-400">
          <p class="text-indigo-800 font-medium">💡 <strong>In some interviews, time is strictly controlled.</strong> For example, a ten-minute station with five questions leaves little more than a minute per response once marking time is considered. Candidates who ignore this reality often appear unprepared, even when their content is strong.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Why Interviewers Care About Time Control
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers do not mark "timekeeping" explicitly, but answer length affects how they judge you in several domains:</p>

        <div class="grid md:grid-cols-2 gap-4 mb-8">
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
            <h5 class="font-semibold text-slate-800 mb-2">They assess:</h5>
            <ul class="text-sm text-slate-700 space-y-1">
              <li>• Organisation & planning</li>
              <li>• Judgement</li>
              <li>• Insight</li>
              <li>• Professional maturity</li>
            </ul>
          </div>
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
            <h5 class="font-semibold text-slate-800 mb-2">A concise, well-paced answer makes it easier to:</h5>
            <ul class="text-sm text-slate-700 space-y-1">
              <li>• Identify positive indicators</li>
              <li>• Map your response to competencies</li>
              <li>• Score you confidently</li>
            </ul>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Long Answers Create Hidden Risks
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Candidates often speak longer because they feel unsafe stopping. Ironically, this increases risk.</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <p class="text-pink-800 mb-3">Common problems with overlong answers include:</p>
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🎯</span>
              <span>Losing focus and drifting off-question</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🔍</span>
              <span>Introducing unnecessary detail that dilutes key messages</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">⚠️</span>
              <span>Accidentally revealing negative indicators</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">😰</span>
              <span>Appearing anxious or poorly organised</span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">❌ <strong>None of these reflect lack of ability — but all affect scores.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Short Does Not Mean Superficial
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">A two-minute answer can still be rich if it is:</p>

        <div class="bg-emerald-50 p-4 rounded-lg mb-6 border-l-4 border-emerald-400">
          <ul class="space-y-2 text-emerald-700">
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">📋</span>
              <span>Structured</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">🎯</span>
              <span>Focused</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">💪</span>
              <span>Supported by one or two strong examples</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6"><strong>What matters is density of meaning, not volume of words.</strong></p>

        <div class="bg-cyan-50 p-4 rounded-lg mb-8 border-l-4 border-cyan-400">
          <p class="text-cyan-800 font-medium">🎯 <strong>Strong candidates learn to say: The right thing, At the right time, In the right amount.</strong></p>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">⏱️</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Answer length is not about politeness or style. It is about professional judgement under constraint.</p>
          <p class="text-indigo-100 font-semibold">If you can deliver a clear, relevant, well-structured answer within two minutes, you signal that you can: Think under pressure, Prioritise effectively, Communicate safely. Those are exactly the qualities interviewers are looking for.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">⏱️</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 11,
      slug: 'marking-scheme-explained',
      category: 'Interview Insights',
      title: 'The Marking Scheme Explained: How Interviewers Turn Behaviour Into Scores',
      excerpt: 'Candidates often leave interviews wondering how their answers were converted into numbers. The reality is that medical interviews use a behaviour-based marking scheme designed to translate complex human responses into structured, comparable scores.',
      author: 'Medical Interview Team',
      date: 'Jan 18, 2025',
      readTime: '8 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl border-l-4 border-amber-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">📊</span>
            </div>
            <h3 class="text-lg font-bold text-amber-800">How Answers Become Numbers</h3>
          </div>
          <p class="text-slate-700 italic">Candidates often leave interviews wondering how their answers were converted into numbers. The reality is that medical interviews use a behaviour-based marking scheme designed to translate complex human responses into structured, comparable scores.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Structured Marking Exists
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Structured interviews are built to be fair. Each question is written with a specific purpose: to test one or more competencies. Interviewers are not free to explore personal interests or pursue ad hoc lines of questioning. Instead, they assess answers against predefined behavioural criteria.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 mb-3 font-medium">This approach allows panels to:</p>
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">⚖️</span>
              <span>Compare candidates objectively</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🛡️</span>
              <span>Reduce bias and inconsistency</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📏</span>
              <span>Ensure patient safety standards are upheld</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">However, structure does not eliminate judgement entirely. Interviewers still interpret behaviour — but within clear boundaries.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          There Is No "Perfect Answer"
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">A common misconception is that every interview question has a correct response. In reality, interviewers are not searching for rehearsed answers, but for evidence of professional thinking.</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <p class="text-blue-800 mb-3">High-scoring candidates demonstrate:</p>
          <ul class="space-y-2 text-blue-700">
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🧠</span>
              <span>Reasoned judgement</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🔍</span>
              <span>Insight into complexity</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">⚖️</span>
              <span>Awareness of consequences</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🔄</span>
              <span>Ability to reflect and adapt</span>
            </li>
          </ul>
        </div>

        <div class="bg-green-50 p-4 rounded-lg mb-8 border-l-4 border-green-400">
          <p class="text-green-800 font-medium">✅ <strong>An intelligent discussion supported by relevant examples will always score higher than a rigid, memorised response.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Positive and Negative Indicators: The Real Scoring Engine
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Behind each interview question sits a list of positive and negative indicators. These are not visible to candidates, but they guide every mark awarded.</p>

        <div class="grid md:grid-cols-2 gap-6 mb-8">
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
            <h5 class="font-semibold text-green-800 mb-3">Positive indicators reflect behaviours such as:</h5>
            <ul class="text-sm text-green-700 space-y-1">
              <li>• Taking a constructive and solution-focused approach</li>
              <li>• Considering patient safety and wider system impact</li>
              <li>• Recognising personal limitations</li>
              <li>• Willingness to seek help appropriately</li>
              <li>• Managing stress using healthy, effective strategies</li>
            </ul>
          </div>
          <div class="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
            <h5 class="font-semibold text-red-800 mb-3">Negative indicators signal concern, for example:</h5>
            <ul class="text-sm text-red-700 space-y-1">
              <li>• Ignoring patient safety implications</li>
              <li>• Attempting to manage serious situations alone</li>
              <li>• Poor stress management strategies</li>
              <li>• Judgmental or inflexible attitudes</li>
              <li>• Failure to involve appropriate colleagues</li>
            </ul>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">These behaviours signal that a candidate is safe, reflective, and collaborative.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Decisive Negative Indicators: Where Candidates Lose Everything
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Some behaviours carry disproportionate weight. These are known as decisive negative indicators because they relate to fundamental professional failures.</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 mb-3">Examples include:</p>
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🛑</span>
              <span>Compromising patient safety</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🚫</span>
              <span>Acting outside competence without escalation</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">⚠️</span>
              <span>Failing to act on serious concerns</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🔇</span>
              <span>Ignoring safeguarding or governance responsibilities</span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">⚠️ <strong>In many interviews, demonstrating a decisive negative indicator can: Score an automatic zero for that station, Trigger failure of the entire interview.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          The 0–4 Scoring Scale (What the Numbers Really Mean)
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers typically score each answer using a five-point scale:</p>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
            <h5 class="font-bold text-slate-800 mb-2">0 – No evidence</h5>
            <p class="text-slate-700 text-sm">No relevant behaviours demonstrated.</p>
          </div>
          <div class="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
            <h5 class="font-bold text-red-800 mb-2">1 – Poor</h5>
            <p class="text-red-700 text-sm">Minimal positive indicators, multiple negative indicators, often decisive.</p>
          </div>
          <div class="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
            <h5 class="font-bold text-orange-800 mb-2">2 – Areas of concern</h5>
            <p class="text-orange-700 text-sm">Some positive behaviours, but overshadowed by significant concerns.</p>
          </div>
          <div class="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
            <h5 class="font-bold text-yellow-800 mb-2">3 – Satisfactory</h5>
            <p class="text-yellow-700 text-sm">Clear positive indicators, minor negatives, none decisive.</p>
          </div>
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
            <h5 class="font-bold text-green-800 mb-2">4 – Good to excellent</h5>
            <p class="text-green-700 text-sm">Strong, consistent positive indicators, few and minor negatives.</p>
          </div>
        </div>

        <div class="bg-blue-50 p-4 rounded-lg mb-8 border-l-4 border-blue-400">
          <p class="text-blue-800 font-medium">💡 <strong>Importantly, a score of 3 is not failure — it represents safe, acceptable performance. A score of 4 requires consistency, insight, and balance.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Why Interviewers Sometimes "Help" You
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">If you miss an important element in your answer, interviewers may probe further. This is not a trick. It is an opportunity.</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-6 border-l-4 border-indigo-400">
          <p class="text-indigo-800 mb-3">For example, if you describe managing a high-pressure situation but fail to mention how you handled stress, an interviewer might ask:</p>
          <div class="bg-white p-3 rounded-lg border-l-4 border-indigo-300 ml-4">
            <p class="text-indigo-700 italic">"What did you find most challenging, and how did you cope?"</p>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This allows you to demonstrate a complete behavioural picture. Candidates who recognise and use probing effectively often improve their score.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">7</span>
          Why Interviews Can Still Feel Inconsistent
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Although many indicators are set nationally, some panels are required to develop local indicators, which can introduce variation between regions or deaneries. This explains why identical answers may be scored differently in different settings.</p>

        <div class="bg-emerald-50 p-4 rounded-lg mb-6 border-l-4 border-emerald-400">
          <p class="text-emerald-800 mb-3">The solution for candidates is not to guess local preferences, but to:</p>
          <ul class="space-y-2 text-emerald-700">
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">🛡️</span>
              <span>Anchor answers in patient safety</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">🔍</span>
              <span>Demonstrate insight and reflection</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">📚</span>
              <span>Align behaviour with professional standards</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">These principles are universally valued.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">8</span>
          The Professional Standard Behind the Scores
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">All marking schemes ultimately reflect the expectations set out in Good Medical Practice by the General Medical Council. Interviewers are trained to recognise behaviours that align with these standards — even when the question appears non-clinical.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🎯</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Interview scores are not about brilliance or confidence. They are about demonstrated behaviour.</p>
          <p class="text-indigo-100 font-semibold">High-scoring candidates think beyond the immediate problem, protect patient safety instinctively, recognise limits and escalate appropriately, reflect honestly under pressure. Once you understand positive and negative indicators, interviews stop feeling arbitrary.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">📊</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 10,
      slug: 'doctor-behind-answers',
      category: 'Interview Insights',
      title: 'The Doctor Behind the Answers: Personality, Values, and Professional Identity',
      excerpt: 'By the time candidates reach a medical interview, most can answer clinical questions competently. Guidelines are recalled, scenarios are managed safely, and structured responses are delivered with confidence.',
      author: 'Medical Interview Team',
      date: 'Jan 15, 2025',
      readTime: '7 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl border-l-4 border-violet-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-violet-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">👤</span>
            </div>
            <h3 class="text-lg font-bold text-violet-800">Who You Are as a Professional</h3>
          </div>
          <p class="text-slate-700 italic">By the time candidates reach a medical interview, most can answer clinical questions competently. Guidelines are recalled, scenarios are managed safely, and structured responses are delivered with confidence.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Interviews as Identity Assessments
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Although medical interviews are framed around competencies, they are also a subtle assessment of professional identity. Interviewers are asking themselves:</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <div class="grid md:grid-cols-3 gap-4">
            <div class="text-center">
              <div class="text-3xl mb-2">🤝</div>
              <p class="text-slate-700 font-medium">What kind of colleague would this person be?</p>
            </div>
            <div class="text-center">
              <div class="text-3xl mb-2">❤️</div>
              <p class="text-slate-700 font-medium">How would patients experience them?</p>
            </div>
            <div class="text-center">
              <div class="text-3xl mb-2">🛡️</div>
              <p class="text-slate-700 font-medium">Would I trust their judgement, honesty, and integrity over time?</p>
            </div>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">These judgements are formed not from a single answer, but from patterns across the interview: how you reason, how you frame responsibility, and how you relate to others.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Personality Is Not Charisma
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">A common misconception is that interviews favour extroverted or highly confident personalities. In reality, interviewers are not scoring charm or dominance. They are assessing traits that support safe, ethical, and collaborative practice.</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
          <p class="text-blue-800 mb-3">Valued personality characteristics include:</p>
          <div class="grid md:grid-cols-2 gap-4">
            <ul class="space-y-2 text-blue-700">
              <li class="flex items-start gap-2">
                <span class="text-blue-500 mt-1">😌</span>
                <span><strong>Calmness under pressure</strong></span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-500 mt-1">🤝</span>
                <span><strong>Approachability and respectfulness</strong></span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-500 mt-1">🧠</span>
                <span><strong>Emotional intelligence</strong></span>
              </li>
            </ul>
            <ul class="space-y-2 text-blue-700">
              <li class="flex items-start gap-2">
                <span class="text-blue-500 mt-1">💭</span>
                <span><strong>Thoughtfulness and balance</strong></span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-500 mt-1">🚧</span>
                <span><strong>Professional boundaries</strong></span>
              </li>
            </ul>
          </div>
        </div>

        <div class="bg-green-50 p-4 rounded-lg mb-8 border-l-4 border-green-400">
          <p class="text-green-800 font-medium">✅ <strong>A quiet, reflective candidate who communicates clearly often scores higher than a charismatic candidate who lacks insight or restraint.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Values Are Assessed Indirectly
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers rarely ask, "What are your values?" Instead, values emerge through:</p>

        <div class="grid md:grid-cols-2 gap-4 mb-8">
          <div class="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
            <ul class="text-sm text-orange-700 space-y-2">
              <li class="flex items-start gap-2">
                <span class="text-orange-500 mt-1">🛡️</span>
                <span>How you prioritise patient safety</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-orange-500 mt-1">👥</span>
                <span>How you speak about colleagues</span>
              </li>
            </ul>
          </div>
          <div class="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
            <ul class="text-sm text-orange-700 space-y-2">
              <li class="flex items-start gap-2">
                <span class="text-orange-500 mt-1">🔄</span>
                <span>How you describe mistakes and conflict</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-orange-500 mt-1">⚖️</span>
                <span>How you balance efficiency with compassion</span>
              </li>
            </ul>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">For example, when discussing a difficult colleague, do you show blame or professionalism? When describing a busy clinic, do you prioritise speed or patient understanding? These choices reveal values far more reliably than declarations.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Alignment With Professional Standards
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Professional identity in medicine is not subjective. It is anchored in shared standards such as Good Medical Practice, published by the General Medical Council.</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 mb-3">Interviewers look for consistent alignment with principles such as:</p>
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🙏</span>
              <span>Respect for patients as individuals</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🤝</span>
              <span>Honesty, integrity, and accountability</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🛡️</span>
              <span>Commitment to patient safety and quality</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">👥</span>
              <span>Effective teamwork and communication</span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">⚠️ <strong>A candidate may demonstrate excellent knowledge, but if their reasoning conflicts with these principles, concern is raised.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          NHS Values in Action, Not in Name
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Similarly, interviewers assess alignment with NHS values not by memorisation, but by application. Candidates score highly when they embed values into their decision-making, rather than listing them.</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-pink-50 p-4 rounded-lg border-l-4 border-pink-400 text-center">
            <div class="text-2xl mb-2">🤝</div>
            <h5 class="font-semibold text-pink-800 mb-2">"Working together for patients"</h5>
            <p class="text-pink-700 text-sm">appears when you involve the multidisciplinary team</p>
          </div>
          <div class="bg-pink-50 p-4 rounded-lg border-l-4 border-pink-400 text-center">
            <div class="text-2xl mb-2">❤️</div>
            <h5 class="font-semibold text-pink-800 mb-2">"Respect and dignity"</h5>
            <p class="text-pink-700 text-sm">appears in how you handle vulnerable patients</p>
          </div>
          <div class="bg-pink-50 p-4 rounded-lg border-l-4 border-pink-400 text-center">
            <div class="text-2xl mb-2">👥</div>
            <h5 class="font-semibold text-pink-800 mb-2">"Everyone counts"</h5>
            <p class="text-pink-700 text-sm">appears in equitable decision-making</p>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Consistency Builds Trust
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">One of the strongest indicators of a solid professional identity is consistency. Interviewers look for alignment between:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400 text-center">
            <div class="text-2xl mb-2">💬</div>
            <p class="text-indigo-700 font-medium">Different answers</p>
          </div>
          <div class="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400 text-center">
            <div class="text-2xl mb-2">🏥</div>
            <p class="text-indigo-700 font-medium">Different stations</p>
          </div>
          <div class="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400 text-center">
            <div class="text-2xl mb-2">⚖️</div>
            <p class="text-indigo-700 font-medium">Clinical and non-clinical scenarios</p>
          </div>
        </div>

        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-r-lg">
          <p class="text-yellow-800 font-medium">⚠️ <strong>A candidate who emphasises teamwork in one answer but describes solo decision-making in another appears unreliable. Consistency, even if imperfect, builds trust.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">7</span>
          Authenticity Over Performance
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Candidates sometimes attempt to present an "ideal doctor" persona. This often backfires. Interviewers are skilled at detecting rehearsed or exaggerated responses.</p>

        <div class="bg-emerald-50 p-4 rounded-lg mb-6 border-l-4 border-emerald-400">
          <p class="text-emerald-800 mb-3">Authenticity shows through:</p>
          <ul class="space-y-2 text-emerald-700">
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">🔍</span>
              <span>Honest reflection</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">⚖️</span>
              <span>Balanced self-appraisal</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">🎭</span>
              <span>Willingness to acknowledge uncertainty</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">❤️</span>
              <span>Genuine concern for patient welfare</span>
            </li>
          </ul>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🎯</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Medical interviews are not just about selecting capable doctors. They are about selecting the right kind of doctors.</p>
          <p class="text-indigo-100 font-semibold">Behind every answer, interviewers are asking: "Is this someone who embodies the values of the profession?" Candidates who succeed are those whose personality supports safe practice, values align with professional standards, and identity is consistent, reflective, and trustworthy.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">👤</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 9,
      slug: 'under-pressure-reveal',
      category: 'Interview Insights',
      title: 'Under Pressure: What Stress, Mistakes, and Uncertainty Reveal About You',
      excerpt: 'When candidates are asked about stress, mistakes, or moments of uncertainty, many assume these questions are designed to catch them out. In reality, these are among the most informative questions in a medical interview.',
      author: 'Medical Interview Team',
      date: 'Jan 12, 2025',
      readTime: '8 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl border-l-4 border-red-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">⚡</span>
            </div>
            <h3 class="text-lg font-bold text-red-800">What Happens When Conditions Aren't Perfect</h3>
          </div>
          <p class="text-slate-700 italic">When candidates are asked about stress, mistakes, or moments of uncertainty, many assume these questions are designed to catch them out. In reality, these are among the most informative questions in a medical interview.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Pressure Questions Exist at All
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Medicine is practised in environments where:</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <div class="grid md:grid-cols-3 gap-4">
            <div class="text-center">
              <div class="text-3xl mb-2">⏰</div>
              <p class="text-slate-700 font-medium">Time is limited</p>
            </div>
            <div class="text-center">
              <div class="text-3xl mb-2">📊</div>
              <p class="text-slate-700 font-medium">Information is incomplete</p>
            </div>
            <div class="text-center">
              <div class="text-3xl mb-2">🎯</div>
              <p class="text-slate-700 font-medium">Stakes are high</p>
            </div>
          </div>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">Interviewers know that technical competence alone is insufficient in such settings. They use pressure-based questions to assess whether you can:</p>

        <div class="bg-blue-50 p-4 rounded-lg mb-8 border-l-4 border-blue-400">
          <ul class="space-y-2 text-blue-700">
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🧠</span>
              <span>Maintain judgement under stress</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🎯</span>
              <span>Recognise your limits</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🛡️</span>
              <span>Act safely despite uncertainty</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">🔄</span>
              <span>Reflect rather than react</span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Stress Does Not Disqualify You — Poor Insight Does
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">One of the biggest misconceptions candidates have is that they must present themselves as someone who "handles stress well" at all times. Interviewers do not expect emotional invulnerability. They expect emotional awareness.</p>

        <div class="grid md:grid-cols-2 gap-4 mb-8">
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
            <h5 class="font-semibold text-green-800 mb-2">High-scoring answers show:</h5>
            <ul class="text-sm text-green-700 space-y-1">
              <li>• Recognition of stress responses</li>
              <li>• Strategies for managing them</li>
              <li>• Insight into when stress affects decision-making</li>
              <li>• Willingness to seek support when needed</li>
            </ul>
          </div>
          <div class="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
            <h5 class="font-semibold text-red-800 mb-2">Low-scoring answers often:</h5>
            <ul class="text-sm text-red-700 space-y-1">
              <li>• Minimise stress entirely</li>
              <li>• Frame it as personal weakness to be hidden</li>
              <li>• Suggest poor self-awareness</li>
            </ul>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Mistake Questions Are About Accountability, Not Failure
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">When interviewers ask about mistakes, they are not asking whether you have made one. They already assume you have.</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 mb-3">What they are evaluating is:</p>
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🔍</span>
              <span>Do you recognise the mistake clearly?</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🛡️</span>
              <span>Did you prioritise patient safety once it occurred?</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📞</span>
              <span>Did you escalate appropriately?</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🔄</span>
              <span>Did you reflect and change future behaviour?</span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 mb-3 font-medium">Candidates lose marks when they:</p>
          <ul class="space-y-2 text-red-700">
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">👥</span>
              <span>Blame systems or other people without reflection</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">📉</span>
              <span>Downplay the significance of the error</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">🛡️</span>
              <span>Focus on justification rather than learning</span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Uncertainty Is Inevitable — How You Handle It Is Not
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Uncertainty is inherent in clinical medicine. Interviewers are keenly aware of this, which is why they listen closely for how candidates talk about ambiguous situations.</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-6 border-l-4 border-indigo-400">
          <p class="text-indigo-800 mb-3">They are not looking for certainty. They are looking for:</p>
          <ul class="space-y-2 text-indigo-700">
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">⚠️</span>
              <span>Risk awareness</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">⚖️</span>
              <span>Proportionate decision-making</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">📞</span>
              <span>Appropriate escalation</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">👥</span>
              <span>Use of guidelines, colleagues, and systems</span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Asking for Help Is a Strength, Not a Weakness
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">One of the clearest indicators of professional maturity is knowing when to ask for help.</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <p class="text-pink-800 mb-3">Interviewers consistently reward candidates who:</p>
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🎯</span>
              <span>Recognise the limits of their competence</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🤝</span>
              <span>Seek senior input early when appropriate</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">❤️</span>
              <span>Frame escalation as good patient care, not personal inadequacy</span>
            </li>
          </ul>
        </div>

        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-r-lg">
          <p class="text-yellow-800 font-medium">💪 <strong>Candidates who present themselves as managing everything independently may believe they are showing confidence. In reality, they often signal poor judgement.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Emotional Regulation Under Pressure
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Beyond decision-making, interviewers also assess how you process emotions under stress.</p>

        <div class="bg-purple-50 p-4 rounded-lg mb-6 border-l-4 border-purple-400">
          <p class="text-purple-800 mb-3">They listen for:</p>
          <ul class="space-y-2 text-purple-700">
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">🧠</span>
              <span>Emotional insight without emotional dumping</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">😌</span>
              <span>Calm reasoning rather than impulsivity</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">🤝</span>
              <span>Respectful handling of conflict or distress</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">This is particularly important in scenarios involving:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400 text-center">
            <div class="text-2xl mb-2">👨‍⚕️</div>
            <p class="text-slate-700 font-medium">Difficult colleagues</p>
          </div>
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400 text-center">
            <div class="text-2xl mb-2">😢</div>
            <p class="text-slate-700 font-medium">Distressed patients or relatives</p>
          </div>
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400 text-center">
            <div class="text-2xl mb-2">⚡</div>
            <p class="text-slate-700 font-medium">High-conflict or high-risk environments</p>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">7</span>
          The Professional Framework Behind These Judgements
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The standards used to assess these responses are grounded in professional guidance such as Good Medical Practice issued by the General Medical Council.</p>

        <div class="grid md:grid-cols-2 gap-6 mb-8">
          <div class="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
            <h4 class="font-bold text-blue-800 mb-3 flex items-center gap-2">
              <span class="text-blue-600">📋</span>
              Key principles include:
            </h4>
            <ul class="text-blue-700 text-sm space-y-1">
              <li>• Working within limits of competence</li>
              <li>• Acting to protect patient safety</li>
              <li>• Being honest, reflective, and accountable</li>
              <li>• Working effectively with colleagues</li>
            </ul>
          </div>
          <div class="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
            <h4 class="font-bold text-green-800 mb-3 flex items-center gap-2">
              <span class="text-green-600">🔍</span>
              Your answers are evaluated against:
            </h4>
            <p class="text-green-700 text-sm">these principles—even if they are never mentioned explicitly.</p>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">8</span>
          What Interviewers Ultimately Want to See
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Under pressure, interviewers are not asking:</p>

        <div class="bg-red-50 p-4 rounded-lg mb-6 border-l-4 border-red-400">
          <p class="text-red-800 font-medium text-center">❌ "Is this candidate impressive?"</p>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">They are asking:</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <p class="text-green-800 font-medium text-center">✅ "Is this candidate safe, reflective, and trustworthy when things go wrong?"</p>
        </div>

        <div class="bg-cyan-50 p-4 rounded-lg mb-6 border-l-4 border-cyan-400">
          <p class="text-cyan-800 mb-3 font-medium">Candidates who score highly are those who demonstrate:</p>
          <ul class="space-y-2 text-cyan-700">
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">😌</span>
              <span>Calm reasoning under stress</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">🔍</span>
              <span>Honest reflection on mistakes</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">🎯</span>
              <span>Mature handling of uncertainty</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">🛡️</span>
              <span>Commitment to patient safety over ego</span>
            </li>
          </ul>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🎯</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Stress, mistakes, and uncertainty do not weaken your interview performance. Avoiding them does.</p>
          <p class="text-indigo-100 font-semibold">Handled well, these questions allow you to show: Professional maturity, Insight and self-awareness, Alignment with core medical values. In many interviews, this is where final decisions are made.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">⚡</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 8,
      slug: 'attitude-over-expertise',
      category: 'Interview Insights',
      title: 'More Than Knowledge: Why Attitude Often Outweighs Expertise',
      excerpt: 'Many candidates approach medical interviews believing that knowledge is the main currency. They revise guidelines, rehearse clinical scenarios, and memorise definitions—yet still underperform.',
      author: 'Medical Interview Team',
      date: 'Jan 10, 2025',
      readTime: '6 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-l-4 border-emerald-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">💭</span>
            </div>
            <h3 class="text-lg font-bold text-emerald-800">Knowledge Opens Doors; Attitude Gets You Through Them</h3>
          </div>
          <p class="text-slate-700 italic">Many candidates approach medical interviews believing that knowledge is the main currency. They revise guidelines, rehearse clinical scenarios, and memorise definitions—yet still underperform.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Competence Is Assumed; Professionalism Is Tested
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">By the time you reach interview, interviewers already assume a baseline level of clinical competence. Your application, exams, and training history have already demonstrated that you are capable of doing the job at a fundamental level.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 mb-3 font-medium">The interview, therefore, shifts focus:</p>
          <div class="grid md:grid-cols-2 gap-4">
            <div class="bg-red-50 p-3 rounded-lg border-l-4 border-red-400">
              <h5 class="font-semibold text-red-800 mb-2">From:</h5>
              <p class="text-red-700 text-sm">❌ What you know</p>
            </div>
            <div class="bg-green-50 p-3 rounded-lg border-l-4 border-green-400">
              <h5 class="font-semibold text-green-800 mb-2">To:</h5>
              <p class="text-green-700 text-sm">✅ How you think and behave</p>
            </div>
          </div>
        </div>

        <div class="bg-blue-50 p-4 rounded-lg mb-8">
          <p class="text-blue-800 font-medium text-center">🤔 <strong>Interviewers are asking themselves: "Is this someone I would trust at 3 a.m. with a complex, uncertain, and emotionally charged situation?"</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          What Interviewers Mean by "Attitude"
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Attitude in interviews is not about enthusiasm alone. It is a composite of behaviours, values, and self-awareness that emerges across your answers.</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-6 border-l-4 border-indigo-400">
          <p class="text-indigo-800 mb-3">Key elements include:</p>
          <div class="grid md:grid-cols-2 gap-4">
            <ul class="space-y-2 text-indigo-700">
              <li class="flex items-start gap-2">
                <span class="text-indigo-500 mt-1">🙏</span>
                <span><strong>Humility</strong> – recognising limits and asking for help appropriately</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-indigo-500 mt-1">🎯</span>
                <span><strong>Responsibility</strong> – taking ownership without defensiveness</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-indigo-500 mt-1">🔄</span>
                <span><strong>Reflectiveness</strong> – learning from mistakes and feedback</span>
              </li>
            </ul>
            <ul class="space-y-2 text-indigo-700">
              <li class="flex items-start gap-2">
                <span class="text-indigo-500 mt-1">❤️</span>
                <span><strong>Respect</strong> – towards patients, families, and colleagues</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-indigo-500 mt-1">🛡️</span>
                <span><strong>Safety awareness</strong> – prioritising patient welfare over ego</span>
              </li>
            </ul>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Why Overconfidence Is a Red Flag
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">One of the most common ways strong candidates lose marks is through overconfidence. This may present as:</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <ul class="space-y-2 text-orange-700">
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🏥</span>
              <span>Managing complex cases independently without supervision</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">📉</span>
              <span>Minimising uncertainty or risk</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🚫</span>
              <span>Failing to acknowledge alternative views</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">📞</span>
              <span>Avoiding mention of escalation or senior input</span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">⚠️ <strong>From an interviewer's perspective, this is not impressive — it is concerning. Medicine values safe decision-making, not heroic independence.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Attitude Reveals Itself in How You Handle Uncertainty
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Questions about mistakes, stress, or difficult situations are not designed to expose weakness. They are designed to reveal:</p>

        <div class="grid md:grid-cols-3 gap-4 mb-8">
          <div class="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-400 text-center">
            <div class="text-2xl mb-2">🎭</div>
            <h5 class="font-semibold text-teal-800 mb-2">How you respond</h5>
            <p class="text-teal-700 text-sm">when things don't go to plan</p>
          </div>
          <div class="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-400 text-center">
            <div class="text-2xl mb-2">🛡️</div>
            <h5 class="font-semibold text-teal-800 mb-2">Whether you become</h5>
            <p class="text-teal-700 text-sm">defensive or reflective</p>
          </div>
          <div class="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-400 text-center">
            <div class="text-2xl mb-2">🎯</div>
            <h5 class="font-semibold text-teal-800 mb-2">Whether you externalise</h5>
            <p class="text-teal-700 text-sm">blame or take responsibility</p>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          The Link Between Attitude and Patient-Centred Care
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Attitude is closely tied to how you view patients—not as problems to be solved, but as people to be cared for.</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <p class="text-pink-800 mb-3">Interviewers listen for:</p>
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">❤️</span>
              <span>Empathy embedded in decision-making</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🤝</span>
              <span>Respect for patient autonomy</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🌍</span>
              <span>Sensitivity to cultural, emotional, and social factors</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">💬</span>
              <span>Willingness to adapt communication</span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Professional Standards Are the Reference Point
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">What counts as the "right attitude" is not subjective. It is grounded in professional standards such as Good Medical Practice published by the General Medical Council.</p>

        <div class="grid md:grid-cols-2 gap-6 mb-8">
          <div class="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
            <h4 class="font-bold text-blue-800 mb-3 flex items-center gap-2">
              <span class="text-blue-600">📚</span>
              These standards emphasise:
            </h4>
            <ul class="text-blue-700 text-sm space-y-1">
              <li>• Working within limits of competence</li>
              <li>• Honesty and integrity</li>
              <li>• Effective teamwork</li>
              <li>• Commitment to patient safety</li>
              <li>• Respect and compassion</li>
            </ul>
          </div>
          <div class="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
            <h4 class="font-bold text-green-800 mb-3 flex items-center gap-2">
              <span class="text-green-600">👥</span>
              Interviewers use these principles:
            </h4>
            <p class="text-green-700 text-sm">consciously or unconsciously—as a benchmark when evaluating your responses.</p>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">7</span>
          Why Attitude Differentiates Strong Candidates
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">At interview, most candidates:</p>

        <div class="bg-emerald-50 p-4 rounded-lg mb-6 border-l-4 border-emerald-400">
          <ul class="space-y-2 text-emerald-700">
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">📖</span>
              <span>Know the same guidelines</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">💬</span>
              <span>Give similar clinical answers</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">📊</span>
              <span>Have comparable experience</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6">What separates those who score highly is how their attitude permeates every answer. They sound safe, thoughtful, reflective, and collaborative — not because they say so, but because their reasoning consistently demonstrates it.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🎯</span>
            The Take-Home Message
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Knowledge gets you shortlisted. Attitude gets you appointed.</p>
          <p class="text-indigo-100 font-semibold">The most successful candidates are not those who try to appear flawless, but those who demonstrate: Insight into their own limitations, Respect for the system they work in, Commitment to learning and patient safety.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">💭</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 7,
      slug: 'hidden-mark-scheme',
      category: 'Interview Insights',
      title: 'The Hidden Mark Scheme: How Interviewers Judge Your Answers',
      excerpt: 'Many candidates leave medical interviews convinced they "answered well" — yet are surprised by average or poor scores. This disconnect happens because interview performance is not judged subjectively.',
      author: 'Medical Interview Team',
      date: 'Jan 08, 2025',
      readTime: '7 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border-l-4 border-purple-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">📊</span>
            </div>
            <h3 class="text-lg font-bold text-purple-800">The Invisible Scoring System</h3>
          </div>
          <p class="text-slate-700 italic">Many candidates leave medical interviews convinced they "answered well" — yet are surprised by average or poor scores. This disconnect happens because interview performance is not judged subjectively, nor is it based on eloquence, confidence, or likeability alone.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Interviews Are Scored, Not Impressed
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Medical interviews are not conversations designed to be enjoyed. They are assessment exercises designed to be scored.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 mb-3 font-medium">Behind each question sits:</p>
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🎯</span>
              <span>A <strong>predefined competency</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📋</span>
              <span>A set of <strong>behavioural indicators</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📊</span>
              <span>A <strong>scoring rubric</strong> (often numerical)</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">👥</span>
              <span>Trained interviewers aligning scores to <strong>evidence, not opinion</strong></span>
            </li>
          </ul>
        </div>

        <div class="bg-blue-50 p-4 rounded-lg mb-8">
          <p class="text-blue-800 font-medium text-center">💡 <strong>Interviewers are not asking: "Did I like that answer?" They are asking: "Did this answer demonstrate the required competency at the expected level?"</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          What Interviewers Are Really Listening For
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">When you answer a question, interviewers are simultaneously listening on multiple levels.</p>

        <div class="grid md:grid-cols-2 gap-4 mb-8">
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
            <h5 class="font-semibold text-slate-800 mb-2">They assess:</h5>
            <ul class="text-sm text-slate-700 space-y-1">
              <li class="flex items-start gap-2">
                <span class="text-slate-500 mt-1">🎯</span>
                <span><strong>Relevance</strong> – Did you answer the question that was actually asked?</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-slate-500 mt-1">📋</span>
                <span><strong>Evidence</strong> – Did you give concrete examples or vague statements?</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-slate-500 mt-1">💭</span>
                <span><strong>Insight</strong> – Do you understand why you acted as you did?</span>
              </li>
            </ul>
          </div>
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
            <h5 class="font-semibold text-slate-800 mb-2">They evaluate:</h5>
            <ul class="text-sm text-slate-700 space-y-1">
              <li class="flex items-start gap-2">
                <span class="text-slate-500 mt-1">⚖️</span>
                <span><strong>Judgement</strong> – Were your decisions safe, proportionate, and appropriate?</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-slate-500 mt-1">🔄</span>
                <span><strong>Reflection</strong> – Did you learn and adapt from the experience?</span>
              </li>
            </ul>
          </div>
        </div>

        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-r-lg">
          <p class="text-yellow-800 font-medium">⚠️ <strong>Remember:</strong> A fluent answer without evidence scores poorly. A nervous answer with clear structure and insight often scores highly.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Competencies Are Broken Down Into Behaviours
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Each competency (for example, communication, professionalism, or judgement under pressure) is assessed through observable behaviours.</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <p class="text-green-800 mb-3">Interviewers are trained to look for signals such as:</p>
          <ul class="space-y-2 text-green-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🎯</span>
              <span>Awareness of limitations</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🤝</span>
              <span>Willingness to seek senior help</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🛡️</span>
              <span>Prioritisation of patient safety</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">❤️</span>
              <span>Respect for colleagues and patients</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">⚖️</span>
              <span>Ethical and professional reasoning</span>
            </li>
          </ul>
        </div>

        <div class="bg-red-50 p-4 rounded-lg mb-8 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">❌ <strong>You do not score points for claiming these qualities. You score points for demonstrating them through action and reflection.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Why "Good Answers" Sometimes Score Badly
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">A common trap is giving answers that sound impressive but miss the mark.</p>

        <div class="grid md:grid-cols-2 gap-4 mb-8">
          <div class="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
            <h5 class="font-semibold text-orange-800 mb-2">Examples include:</h5>
            <ul class="text-sm text-orange-700 space-y-1">
              <li class="flex items-start gap-2">
                <span class="text-orange-500 mt-1">🔬</span>
                <span>Overly technical answers that ignore patient-centred concerns</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-orange-500 mt-1">🦸</span>
                <span>Hero narratives that exclude teamwork or supervision</span>
              </li>
            </ul>
          </div>
          <div class="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
            <h5 class="font-semibold text-orange-800 mb-2">Other pitfalls:</h5>
            <ul class="text-sm text-orange-700 space-y-1">
              <li class="flex items-start gap-2">
                <span class="text-orange-500 mt-1">😎</span>
                <span>Confident decisions without risk awareness</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-orange-500 mt-1">😢</span>
                <span>Emotional stories without reflection or learning</span>
              </li>
            </ul>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Structure Beats Style Every Time
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviewers score answers more easily when they are well structured. This is not about rehearsed scripts, but about clarity of thinking.</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 mb-3">High-scoring answers typically include:</p>
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📝</span>
              <span>Brief context</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🔍</span>
              <span>Clear explanation of actions taken</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">✅</span>
              <span>Justification of decisions</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🔄</span>
              <span>Reflection on outcomes and learning</span>
            </li>
          </ul>
        </div>

        <div class="bg-indigo-50 p-4 rounded-lg mb-8">
          <p class="text-indigo-800 font-medium text-center">🎯 <strong>This structure allows interviewers to clearly map your answer to the competency being tested. When they can't map it, they can't score it — no matter how good it sounded.</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          The Role of Professional Values in Scoring
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Even when a question appears purely clinical or practical, interviewers are assessing alignment with professional standards such as those outlined in Good Medical Practice by the General Medical Council.</p>

        <div class="grid md:grid-cols-2 gap-4 mb-8">
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
            <h5 class="font-semibold text-slate-800 mb-2">This includes:</h5>
            <ul class="text-sm text-slate-700 space-y-1">
              <li class="flex items-start gap-2">
                <span class="text-slate-500 mt-1">🛡️</span>
                <span>Patient safety as a priority</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-slate-500 mt-1">📏</span>
                <span>Working within limits of competence</span>
              </li>
            </ul>
          </div>
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
            <h5 class="font-semibold text-slate-800 mb-2">And:</h5>
            <ul class="text-sm text-slate-700 space-y-1">
              <li class="flex items-start gap-2">
                <span class="text-slate-500 mt-1">🤝</span>
                <span>Honesty, integrity, and accountability</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-slate-500 mt-1">💬</span>
                <span>Effective teamwork and communication</span>
              </li>
            </ul>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">7</span>
          What Interviewers Cannot Score — Even If They Like It
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">There are things interviewers may appreciate but cannot award marks for, such as:</p>

        <div class="grid md:grid-cols-2 gap-4 mb-8">
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400">
            <ul class="text-sm text-emerald-700 space-y-2">
              <li class="flex items-start gap-2">
                <span class="text-emerald-500 mt-1">✨</span>
                <span>Charisma without evidence</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-emerald-500 mt-1">😢</span>
                <span>Personal hardship stories without relevance</span>
              </li>
            </ul>
          </div>
          <div class="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400">
            <ul class="text-sm text-emerald-700 space-y-2">
              <li class="flex items-start gap-2">
                <span class="text-emerald-500 mt-1">💪</span>
                <span>Strong opinions unsupported by reasoning</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-emerald-500 mt-1">😎</span>
                <span>Confidence unaccompanied by insight</span>
              </li>
            </ul>
          </div>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🎯</span>
            The Key Shift: From Performance to Evidence
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">The biggest mindset change successful candidates make is this: They stop trying to perform well and start trying to demonstrate evidence.</p>
          <p class="text-indigo-100 font-semibold">Every answer becomes an opportunity to show: How you think, How you act, How you reflect, How safe you are. Once you understand the hidden mark scheme, interviews stop feeling unpredictable.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">📊</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 6,
      slug: 'application-to-interview-room',
      category: 'Interview Insights',
      title: 'From Application to Interview Room: How You Are Assessed Before You Ever Speak',
      excerpt: 'Many candidates believe that medical interviews are won or lost in the interview room. In reality, a significant proportion of the assessment has already taken place long before you sit in front of a panel.',
      author: 'Medical Interview Team',
      date: 'Jan 05, 2025',
      readTime: '8 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl border-l-4 border-indigo-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">📋</span>
            </div>
            <h3 class="text-lg font-bold text-indigo-800">Assessment Begins Long Before You Speak</h3>
          </div>
          <p class="text-slate-700 italic">Many candidates believe that medical interviews are won or lost in the interview room. In reality, a significant proportion of the assessment has already taken place long before you sit in front of a panel.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Structured Interviews: Why "Just Being Yourself" Is Not Enough
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Modern medical interviews are structured, not conversational. Unlike older, unstructured interviews that resembled informal discussions, today's interviews are carefully designed to test specific competencies and behaviours. Every question has a purpose, and every answer is evaluated against a marking framework.</p>

        <div class="bg-amber-50 border-l-4 border-amber-400 p-4 mb-8 rounded-r-lg">
          <p class="text-amber-800 font-medium">🎯 <strong>Key Insight:</strong> Despite the apparent complexity of the interview process, interviewers are consistently trying to answer three fundamental questions:</p>
        </div>

        <div class="grid gap-4 mb-8">
          <div class="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
            <div class="flex items-start gap-4">
              <div class="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span class="text-white text-lg font-bold">1</span>
              </div>
              <div>
                <h4 class="font-bold text-green-800 mb-2">Are you competent enough to do the job?</h4>
                <p class="text-green-700">Do you have the required knowledge, skills, and experience to practise safely and effectively?</p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
            <div class="flex items-start gap-4">
              <div class="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span class="text-white text-lg font-bold">2</span>
              </div>
              <div>
                <h4 class="font-bold text-blue-800 mb-2">Do you have the right attitude?</h4>
                <p class="text-blue-700">Are you motivated, reflective, resilient, and genuinely committed to the specialty?</p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
            <div class="flex items-start gap-4">
              <div class="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span class="text-white text-lg font-bold">3</span>
              </div>
              <div>
                <h4 class="font-bold text-purple-800 mb-2">Will you fit in?</h4>
                <p class="text-purple-700">Do you demonstrate professionalism, emotional intelligence, and the ability to work well with patients and colleagues?</p>
              </div>
            </div>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          The First Filter: Your Application Form and CV
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Before you ever speak to an interviewer, your application form and CV act as the first and most powerful selection tool. They are not reviewed casually. Instead, they are scored against essential and desirable criteria defined in the official person specification for the role.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6 border-l-4 border-slate-400">
          <p class="text-slate-800 mb-3 font-medium">At this stage:</p>
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📊</span>
              <span><strong>Evidence is valued more than intention</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🎯</span>
              <span><strong>Specific examples outweigh general statements</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🔍</span>
              <span><strong>Alignment with the role matters more than raw achievement</strong></span>
            </li>
          </ul>
        </div>

        <div class="bg-blue-50 p-4 rounded-lg mb-8">
          <p class="text-blue-800 font-medium text-center">💡 <strong>By the time you reach the interview room, interviewers are no longer asking "Is this candidate suitable on paper?" They are asking "Does this candidate confirm what the application suggests?"</strong></p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          The National Person Specification: The Blueprint You Cannot Ignore
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Every specialty and grade has its own National Person Specification, freely available online. This document outlines the skills, competencies, and behaviours that interviewers are explicitly instructed to assess.</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <p class="text-green-800 mb-3">The most critical section for candidates is the <strong>Selection Criteria</strong>, which typically covers areas such as:</p>
          <ul class="space-y-2 text-green-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🩺</span>
              <span>Clinical skills and judgement</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📚</span>
              <span>Academic and quality improvement activity</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">💬</span>
              <span>Communication and empathy</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📅</span>
              <span>Organisation and prioritisation</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🧠</span>
              <span>Judgement under pressure</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">❤️</span>
              <span>Patient-centred care</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🏥</span>
              <span>Alignment with NHS values</span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Competencies Are Tested Through Behaviour, Not Theory
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">One common misconception is that interviewers are simply testing what you know. In fact, they are far more interested in how you behave, how you think, and how you act under real-world conditions.</p>

        <div class="grid md:grid-cols-2 gap-4 mb-8">
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
            <h5 class="font-semibold text-slate-800 mb-2">Clinical competence is explored through:</h5>
            <ul class="text-sm text-slate-700 space-y-1">
              <li>• Prioritisation</li>
              <li>• Decision-making</li>
              <li>• Risk awareness</li>
            </ul>
          </div>
          <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
            <h5 class="font-semibold text-slate-800 mb-2">Professionalism is inferred from:</h5>
            <ul class="text-sm text-slate-700 space-y-1">
              <li>• How you describe mistakes</li>
              <li>• How you handle uncertainty</li>
              <li>• How you respond to feedback</li>
            </ul>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Two Core Documents That Shape the Entire Interview
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">To truly understand what interviewers are looking for, you must be familiar with two key documents:</p>

        <div class="grid md:grid-cols-2 gap-6 mb-8">
          <div class="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
            <h4 class="font-bold text-blue-800 mb-3 flex items-center gap-2">
              <span class="text-blue-600">📋</span>
              National Person Specification
            </h4>
            <p class="text-blue-700 text-sm">For the post you are applying for - defines what good doctors do and how they behave.</p>
          </div>
          <div class="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
            <h4 class="font-bold text-green-800 mb-3 flex items-center gap-2">
              <span class="text-green-600">🏛️</span>
              Good Medical Practice (GMC)
            </h4>
            <p class="text-green-700 text-sm">Published by the General Medical Council - guides ethical, clinical, and professional responses.</p>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Why This Matters for Interview Performance
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">By the time you enter the interview room:</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-6 border-l-4 border-indigo-400">
          <ul class="space-y-2 text-indigo-700">
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">✅</span>
              <span>Your competence has already been partially judged</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">💪</span>
              <span>Your motivation has already been inferred</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">🤝</span>
              <span>Your potential fit has already been hypothesised</span>
            </li>
          </ul>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🎯</span>
            The Interview Is About Confirmation, Not Discovery
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">The interview itself is about confirmation and calibration, not discovery. Candidates who perform best understand what is being assessed, why it is being assessed, and how their answers map to predefined criteria.</p>
          <p class="text-indigo-100 font-semibold">Once you grasp this, interview preparation stops being about memorising answers and starts becoming about demonstrating the right behaviours, consistently and convincingly.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">📚</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 1,
      slug: 'interviewers-looking-for',
      category: 'Interview Insights',
      title: 'What Interviewers Are Really Looking For: The Three Core Selection Questions',
      excerpt: 'Medical interviews can feel overwhelming, but beneath the complexity lies a simple framework. Learn the three core questions that drive every selection decision.',
      author: 'Medical Interview Team',
      date: 'Jan 02, 2025',
      readTime: '6 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border-l-4 border-purple-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🎯</span>
            </div>
            <h3 class="text-lg font-bold text-purple-800">The Hidden Framework of Medical Interviews</h3>
          </div>
          <p class="text-slate-700 italic">Medical interviews can feel overwhelming. Candidates are presented with long question lists, complex scenarios, and tightly timed stations. Yet beneath this apparent complexity lies a surprisingly simple framework.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Can You Do the Job?
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The first and most obvious concern for interview panels is competence. Interviewers must be confident that you can work safely and effectively at the level you are applying for.</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <p class="text-green-800 mb-3">This does not mean proving that you know everything. Instead, they are looking for evidence that you:</p>
          <ul class="space-y-2 text-green-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🩺</span>
              <span>Possess an appropriate level of clinical knowledge</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🧠</span>
              <span>Can apply sound judgement to real-world problems</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">⚡</span>
              <span>Recognise acute illness and take appropriate initial action</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📋</span>
              <span>Prioritise competing clinical demands</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🤝</span>
              <span>Work within your limits and seek help when needed</span>
            </li>
          </ul>
        </div>

        <div class="bg-blue-50 p-4 rounded-lg mb-8">
          <p class="text-blue-800 font-medium text-center">🩺 <strong>Interviewers are asking themselves:</strong> Would we trust this person on the ward at 3 a.m.?</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Do You Have the Right Attitude?
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Being capable is not enough. Medical training programmes are demanding, and interview panels are acutely aware that motivation and professionalism determine long-term success.</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-6 border-l-4 border-indigo-400">
          <p class="text-indigo-800 mb-3">This second question focuses on your attitude to work, including:</p>
          <ul class="space-y-2 text-indigo-700">
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">❤️</span>
              <span>Genuine interest in the specialty</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">📈</span>
              <span>Willingness to learn and improve</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">💪</span>
              <span>Resilience in the face of pressure</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">📝</span>
              <span>Openness to feedback and criticism</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-indigo-500 mt-1">🔍</span>
              <span>Insight into your own strengths and limitations</span>
            </li>
          </ul>
        </div>

        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-r-lg">
          <p class="text-yellow-800 font-medium">💡 <strong>Pro tip:</strong> Questions about mistakes, stress, or weaknesses are not traps. They are opportunities to demonstrate reflection, honesty, and maturity.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Will You Fit In?
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The final question is often underestimated, yet it plays a crucial role in selection decisions. Medicine is fundamentally a team-based profession, and interviewers must consider how you will function within existing systems.</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <p class="text-pink-800 mb-3">"Fitting in" does not mean being the same as everyone else. It means demonstrating that you can:</p>
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">💬</span>
              <span>Communicate clearly and respectfully</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🤝</span>
              <span>Build rapport with patients and colleagues</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">⚖️</span>
              <span>Handle conflict professionally</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">❤️</span>
              <span>Show empathy and cultural sensitivity</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🏥</span>
              <span>Uphold shared professional values</span>
            </li>
          </ul>
        </div>

        <div class="bg-emerald-50 p-4 rounded-lg mb-8">
          <p class="text-emerald-800 font-medium text-center">👁️ <strong>Remember:</strong> Your personality, communication style, and emotional awareness are being assessed from the moment you enter the room.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          How These Questions Shape the Interview
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Although interview questions may appear diverse — clinical cases, ethical dilemmas, academic discussions, or role play — they are simply different lenses through which the same three questions are examined.</p>

        <div class="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 rounded-xl border border-teal-200 mb-8">
          <h4 class="font-bold text-teal-800 mb-3">🎭 A single scenario may test:</h4>
          <div class="grid md:grid-cols-3 gap-4">
            <div class="bg-teal-100 p-3 rounded-lg text-center">
              <span class="text-2xl mb-2 block">🩺</span>
              <p class="font-semibold text-teal-800">Competence</p>
              <p class="text-sm text-teal-700">Clinical judgement</p>
            </div>
            <div class="bg-teal-100 p-3 rounded-lg text-center">
              <span class="text-2xl mb-2 block">💪</span>
              <p class="font-semibold text-teal-800">Attitude</p>
              <p class="text-sm text-teal-700">Response under pressure</p>
            </div>
            <div class="bg-teal-100 p-3 rounded-lg text-center">
              <span class="text-2xl mb-2 block">🤝</span>
              <p class="font-semibold text-teal-800">Fit</p>
              <p class="text-sm text-teal-700">Communication & teamwork</p>
            </div>
          </div>
          <p class="text-teal-800 text-sm mt-3 text-center font-medium">All at once!</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          Preparing With Purpose
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Effective preparation starts by mapping your experiences to these three areas. Every example you use should demonstrate:</p>

        <div class="grid md:grid-cols-3 gap-6 mb-8">
          <div class="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
            <h5 class="font-bold text-orange-800 mb-2 flex items-center gap-2">
              <span class="text-orange-600">🔧</span>
              What you did
            </h5>
            <p class="text-orange-700 text-sm">Competence</p>
          </div>
          <div class="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
            <h5 class="font-bold text-orange-800 mb-2 flex items-center gap-2">
              <span class="text-orange-600">💭</span>
              Why you acted that way
            </h5>
            <p class="text-orange-700 text-sm">Attitude</p>
          </div>
          <div class="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
            <h5 class="font-bold text-orange-800 mb-2 flex items-center gap-2">
              <span class="text-orange-600">👥</span>
              How you interacted with others
            </h5>
            <p class="text-orange-700 text-sm">Fit</p>
          </div>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🚀</span>
            What's Next in This Series
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">In the next blog, we will look at how shortlisting works before the interview even begins, and why understanding scoring systems and person specifications can give you a significant advantage.</p>
          <p class="text-indigo-100 font-semibold">Stay tuned for detailed strategies on preparing for every stage of the NHS interview process!</p>
        </div>


        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">👨‍⚕️</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Medical Interview Team</p>
              <p class="text-slate-700 font-medium">The Medical Interview Team provides comprehensive guidance based on official NHS recruitment best practices and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 2,
      slug: 'medical-interviews-structured-selection',
      category: 'Interview Insights',
      title: 'How Medical Interviews Really Work: Understanding Structured Selection',
      excerpt: 'Medical interviews have evolved from casual conversations to highly structured assessments. Learn the three fundamental questions behind every interview and how to prepare effectively.',
      author: 'Medical Interview Team',
      date: 'Dec 30, 2024',
      readTime: '8 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl border-l-4 border-indigo-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🎯</span>
            </div>
            <h3 class="text-lg font-bold text-indigo-800">The Hidden Structure of Medical Interviews</h3>
          </div>
          <p class="text-slate-700 italic">Many candidates walk into medical interviews expecting an open conversation. In reality, modern medical interviews are carefully designed, highly structured assessments built to evaluate specific competencies.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          From Conversation to Structured Assessment
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Older-style medical interviews often relied on unstructured discussions, shaped largely by the interests and impressions of the interview panel. Today, this approach has been replaced by structured interviews. In a structured format, interviewers follow predefined questions, scoring frameworks, and competency domains to ensure fairness and consistency across candidates.</p>

        <div class="bg-amber-50 border-l-4 border-amber-400 p-4 mb-8 rounded-r-lg">
          <p class="text-amber-800 font-medium">⚖️ <strong>Key Insight:</strong> This means that interviewers are not simply reacting to what you say. They are actively listening for evidence that you meet clearly defined selection criteria.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          The Three Questions Behind Every Interview
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Despite the complexity of modern interview formats, most selection decisions are driven by three fundamental questions:</p>

        <div class="grid gap-4 mb-8">
          <div class="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
            <div class="flex items-start gap-4">
              <div class="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span class="text-white text-lg font-bold">1</span>
              </div>
              <div>
                <h4 class="font-bold text-green-800 mb-2">Can you do the job?</h4>
                <p class="text-green-700">This relates to your clinical competence, knowledge, and experience.</p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
            <div class="flex items-start gap-4">
              <div class="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span class="text-white text-lg font-bold">2</span>
              </div>
              <div>
                <h4 class="font-bold text-blue-800 mb-2">Do you have the right attitude?</h4>
                <p class="text-blue-700">Interviewers look for motivation, commitment, professionalism, and genuine interest in the specialty.</p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
            <div class="flex items-start gap-4">
              <div class="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span class="text-white text-lg font-bold">3</span>
              </div>
              <div>
                <h4 class="font-bold text-purple-800 mb-2">Will you fit in?</h4>
                <p class="text-purple-700">This concerns interpersonal skills, communication style, and whether you are likely to work effectively with patients and colleagues.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-slate-50 p-4 rounded-lg mb-8">
          <p class="text-slate-800 font-medium text-center">Every interview question — no matter how it is phrased — is designed to gather evidence in one or more of these areas.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Why Your CV Is Only the Beginning
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">By the time you reach the interview stage, some assessment has already taken place. Application forms and CVs are used to screen candidates against essential and desirable criteria. These criteria are defined in official person specifications and scored to create a shortlist.</p>

        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <p class="text-orange-800 mb-3">The interview itself serves a different purpose:</p>
          <ul class="space-y-2 text-orange-700">
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🧠</span>
              <span>It allows the appointment panel to explore <strong>how you think, behave, and respond</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">📊</span>
              <span>Not just <strong>what you have done</strong></span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🎯</span>
              <span>Strong performance depends on aligning your answers with the criteria being assessed</span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          The Role of the National Person Specification
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Every specialty and training level has an associated National Person Specification. This document outlines the skills, behaviours, and attributes expected of successful candidates. While many applicants are aware of its existence, fewer use it effectively in interview preparation.</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 mb-3">The most critical section for interviews is the <strong>Selection Criteria</strong>:</p>
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📋</span>
              <span>This section explains exactly what interviewers are assessing and why</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🔄</span>
              <span>Criteria may vary slightly between specialties and grades, but the underlying principles remain consistent</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📐</span>
              <span>In essence, the person specification acts as a blueprint for the interview</span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">5</span>
          What Interviewers Are Really Assessing
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Although questions vary, selection criteria tend to fall into recurring domains:</p>

        <div class="grid md:grid-cols-2 gap-4 mb-8">
          <div class="space-y-4">
            <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
              <h5 class="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span class="text-slate-600">⚕️</span>
                Clinical competence and safe decision-making
              </h5>
            </div>
            <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
              <h5 class="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span class="text-slate-600">📚</span>
                Academic engagement, audit, and teaching
              </h5>
            </div>
            <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
              <h5 class="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span class="text-slate-600">🧠</span>
                Judgement under pressure and self-awareness
              </h5>
            </div>
            <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
              <h5 class="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span class="text-slate-600">💬</span>
                Communication skills, empathy, and professionalism
              </h5>
            </div>
          </div>
          <div class="space-y-4">
            <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
              <h5 class="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span class="text-slate-600">📅</span>
                Organisation, planning, and prioritisation
              </h5>
            </div>
            <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
              <h5 class="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span class="text-slate-600">👁️</span>
                Situational awareness and patient safety
              </h5>
            </div>
            <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
              <h5 class="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span class="text-slate-600">🤝</span>
                Patient-centred care and advocacy
              </h5>
            </div>
            <div class="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
              <h5 class="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span class="text-slate-600">🏥</span>
                Alignment with NHS values
              </h5>
            </div>
          </div>
        </div>

        <div class="bg-blue-50 p-4 rounded-lg mb-8">
          <p class="text-blue-800 font-medium text-center">Each question is an opportunity to demonstrate one or more of these competencies through clear examples from your own experience.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">6</span>
          Why Guidance Documents Matter
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Two key documents underpin much of what is assessed in medical interviews:</p>

        <div class="grid md:grid-cols-2 gap-6 mb-8">
          <div class="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
            <h4 class="font-bold text-blue-800 mb-3 flex items-center gap-2">
              <span class="text-blue-600">📋</span>
              National Person Specification
            </h4>
            <p class="text-blue-700 text-sm">For the role you are applying for - defines what good doctors do and how they behave.</p>
          </div>
          <div class="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
            <h4 class="font-bold text-green-800 mb-3 flex items-center gap-2">
              <span class="text-green-600">🏛️</span>
              Good Medical Practice (GMC)
            </h4>
            <p class="text-green-700 text-sm">Published by the General Medical Council - guides ethical, clinical, and professional responses.</p>
          </div>
        </div>

        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-r-lg">
          <p class="text-yellow-800 font-medium">💡 <strong>Interview panels expect candidates to be familiar with these principles</strong> — not through memorisation, but through the ability to apply them to real situations.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-bold">7</span>
          Preparing With the Structure in Mind
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Successful interview preparation is not about learning answers by rote. It is about understanding the structure behind the interview and tailoring your responses accordingly. When you know what competencies are being tested, you can shape your examples, reflections, and explanations to directly address what interviewers are looking for.</p>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🚀</span>
            What's Next in This Series
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">In the next posts of this series, we will break down each major selection domain and show how to transform official criteria and guidance into confident, authentic interview answers.</p>
          <p class="text-indigo-100 font-semibold">Stay tuned for detailed strategies on demonstrating clinical competence, communication skills, and NHS values through structured interview responses.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">👨‍⚕️</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Author</p>
              <p class="text-slate-700 font-medium">Dr. James Mitchell is a Consultant Physician and NHS interview panel chair with 20+ years of experience mentoring medical trainees. He specialises in structured selection processes and competency-based interviewing.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 2,
      slug: 'nhs-interview-structure',
      category: 'Interview Preparation',
      title: 'NHS Interview Structure: Stations, Formats, and What to Expect',
      excerpt: 'Master the NHS interview structure with detailed insights into formal and practical stations, from portfolio reviews to clinical governance and role-play scenarios.',
      author: 'Medical Interview Team',
      date: 'Dec 28, 2024',
      readTime: '10 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-l-4 border-blue-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🏗️</span>
            </div>
            <h3 class="text-lg font-bold text-blue-800">Understanding NHS Interview Formats</h3>
          </div>
          <p class="text-slate-700 italic">The structure of the interview varies with each deanery and specialty. Interviews typically consist of a succession of 10-minute sessions at three or four stations, each dealing with different topics.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Interview Formats and Time Management
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Interviews typically consist of a succession of 10-minute sessions at three or four stations, each dealing with different topics. There are variations: some interviews have fewer stations, with more time spent at each; other deaneries have more (up to six). A few have kept the old interview format, i.e. a 30- to 45-minute interview with only one panel.</p>

        <div class="bg-amber-50 border-l-4 border-amber-400 p-4 mb-8 rounded-r-lg">
          <p class="text-amber-800 font-medium">⏰ <strong>Time Management Tip:</strong> In addition, some specialties/deaneries are very strict on time whilst others are less rigid. Before attending your interview, make sure that you know the format adopted for your specialty in that deanery.</p>
        </div>

        <div class="bg-slate-50 p-4 rounded-lg mb-6">
          <h4 class="font-semibold text-slate-800 mb-3">Format Variations:</h4>
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">📊</span>
              <span><strong>Standard Format:</strong> 3-4 stations, 10 minutes each</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">⏱️</span>
              <span><strong>Extended Format:</strong> Fewer stations, more time per station</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🏛️</span>
              <span><strong>Traditional Format:</strong> Single 30-45 minute panel interview</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-slate-500 mt-1">🎯</span>
              <span><strong>Intensive Format:</strong> Up to 6 stations with strict time limits</span>
            </li>
          </ul>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-red-600">⚡</span> Adapting Your Response Style
        </h3>
        <p class="text-slate-700 leading-relaxed mb-4">This will influence the way in which you will need to respond. If the interviewers are allowing 10 minutes for five questions, including the time that it takes to ask the question, then you know that you must provide answers that fit within a 90-second timeframe.</p>

        <div class="bg-red-50 p-4 rounded-lg mb-6 border-l-4 border-red-400">
          <p class="text-red-800 font-medium">This means that you must be more regimented and concise in order not to exceed your time.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Types of Interview Stations
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The stations used at interview can be split between formal stations and practical stations.</p>

        <div class="grid md:grid-cols-2 gap-6 mb-8">
          <div class="bg-green-50 p-6 rounded-xl border border-green-200">
            <h4 class="font-bold text-green-800 mb-3 flex items-center gap-2">
              <span class="text-green-600">💼</span>
              Formal Stations
            </h4>
            <p class="text-green-700">Traditional question and answer sessions with 2-3 interviewers scoring responses.</p>
          </div>
          <div class="bg-purple-50 p-6 rounded-xl border border-purple-200">
            <h4 class="font-bold text-purple-800 mb-3 flex items-center gap-2">
              <span class="text-purple-600">🎭</span>
              Practical Stations
            </h4>
            <p class="text-purple-700">Hands-on approach requiring role play, clinical skills, or interactive scenarios.</p>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Formal Interview Stations
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Formal interview stations comprise two or three interviewers asking pre-determined questions of a candidate and scoring the responses. The stations below are those that are commonly found at CT and ST/Registrar levels.</p>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-orange-600">📁</span> Portfolio Station
        </h3>
        <div class="bg-orange-50 p-4 rounded-lg mb-6 border-l-4 border-orange-400">
          <p class="text-orange-800 mb-3">The portfolio station can be formal or informal. In some deaneries it is strictly marked, whilst in others it is just a chat to ensure that you have matched the required entry criteria.</p>
          <ul class="space-y-2 text-orange-700">
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">⏱️</span>
              <span>You may be asked to summarise your experience in 30 seconds, 2 minutes or 5 minutes</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">💬</span>
              <span>Talk about yourself or go through your CV</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">🔍</span>
              <span>Interviewers may pick on specific areas of your training, asking you to describe what you did in more detail</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-orange-500 mt-1">✅</span>
              <span>They may verify your achievements - bring evidence to support claims made in your application form</span>
            </li>
          </ul>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-teal-600">🤝</span> Motivation, Interpersonal Skills & NHS Issues Station
        </h3>
        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 mb-3">This station deals with your reasons for choosing a specialty or this particular deanery, as well as your communication, team playing and leadership skills.</p>
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🎯</span>
              <span>Questions may be general or may ask for specific examples</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🏥</span>
              <span>You may also be asked questions on NHS hot topics relevant to the specialty</span>
            </li>
          </ul>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-purple-600">📚</span> Academic Station
        </h3>
        <div class="bg-purple-50 p-4 rounded-lg mb-6 border-l-4 border-purple-400">
          <p class="text-purple-800 mb-3">The academic station is designed to test your understanding and experience of teaching, research and audit.</p>
          <div class="grid md:grid-cols-3 gap-4 mb-3">
            <div class="bg-purple-100 p-3 rounded-lg">
              <h5 class="font-semibold text-purple-800 mb-2">Factual Questions</h5>
              <ul class="text-sm text-purple-700 space-y-1">
                <li>• "Describe your experience of the audit process"</li>
                <li>• "How do you critically appraise a paper?"</li>
              </ul>
            </div>
            <div class="bg-purple-100 p-3 rounded-lg">
              <h5 class="font-semibold text-purple-800 mb-2">Reflective Questions</h5>
              <ul class="text-sm text-purple-700 space-y-1">
                <li>• "What did you gain from your research experience?"</li>
              </ul>
            </div>
            <div class="bg-purple-100 p-3 rounded-lg">
              <h5 class="font-semibold text-purple-800 mb-2">Probing Questions</h5>
              <ul class="text-sm text-purple-700 space-y-1">
                <li>• "Do you think all trainees should do research?"</li>
              </ul>
            </div>
          </div>
          <p class="text-purple-700 text-sm mt-3">You may also be asked to critically appraise a paper (20-45 minutes preparation) or discuss a recently read paper.</p>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-pink-600">⚕️</span> Clinical Governance Station
        </h3>
        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <p class="text-pink-800 mb-3">Questions usually revolve around clinical governance and how it affects your practice.</p>
          <div class="space-y-3">
            <div>
              <h5 class="font-semibold text-pink-800">Understanding Clinical Governance:</h5>
              <ul class="text-sm text-pink-700 ml-4 space-y-1">
                <li>• "What do you understand by the term clinical governance?"</li>
                <li>• "How does clinical governance impact on your daily practice?"</li>
              </ul>
            </div>
            <div>
              <h5 class="font-semibold text-pink-800">Evidence-Based Medicine:</h5>
              <ul class="text-sm text-pink-700 ml-4 space-y-1">
                <li>• "What is evidence-based medicine?"</li>
                <li>• "Tell us about a recent paper that you have read"</li>
                <li>• "Tell us about a recent guideline published for this specialty"</li>
              </ul>
            </div>
            <div>
              <h5 class="font-semibold text-pink-800">Risk Management:</h5>
              <ul class="text-sm text-pink-700 ml-4 space-y-1">
                <li>• "Tell us about a recent mistake that you have made"</li>
                <li>• "What happens to critical incident forms once you have submitted them?"</li>
              </ul>
            </div>
          </div>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-red-600">🤔</span> Dilemmas/Difficult Scenarios Station
        </h3>
        <div class="bg-red-50 p-4 rounded-lg mb-6 border-l-4 border-red-400">
          <p class="text-red-800 mb-3">This station tests your ability to handle difficult problems and dilemmas in the workplace.</p>
          <div class="grid md:grid-cols-2 gap-4">
            <div>
              <h5 class="font-semibold text-red-800 mb-2">Common Scenarios:</h5>
              <ul class="text-sm text-red-700 space-y-1">
                <li>• Prioritising multiple urgent tasks</li>
                <li>• Dealing with complications beyond your experience</li>
                <li>• Handling integrity issues (e.g., impaired colleague)</li>
              </ul>
            </div>
            <div>
              <h5 class="font-semibold text-red-800 mb-2">Example Questions:</h5>
              <ul class="text-sm text-red-700 space-y-1">
                <li>• "How do you prioritise urgent ward situations?"</li>
                <li>• "What do you do when complications arise?"</li>
                <li>• "How do you handle an impaired consultant?"</li>
              </ul>
            </div>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Practical Interview Stations
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Practical stations require a much more physical or hands-on involvement from the candidate. They can be found in almost every specialty nowadays.</p>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-emerald-600">💬</span> Communication Station (Role Play)
        </h3>
        <div class="bg-emerald-50 p-4 rounded-lg mb-6 border-l-4 border-emerald-400">
          <p class="text-emerald-800 mb-3">This is more commonly referred to as "role play" or "simulated patient consultation". You are given a small amount of time (5 to 10 minutes) to read a brief, followed by a 10-minute consultation with a patient.</p>
          <div class="bg-emerald-100 p-3 rounded-lg">
            <p class="text-emerald-800 text-sm"><strong>Note:</strong> The patient is normally played by an actor. Communication stations focus on your approach to the patient and problem rather than clinical skills.</p>
          </div>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-indigo-600">📊</span> Presentation Station
        </h3>
        <div class="bg-indigo-50 p-4 rounded-lg mb-6 border-l-4 border-indigo-400">
          <p class="text-indigo-800 mb-3">This station consists of a short presentation (5-10 minutes), often followed by a question-and-answer session (also usually 5-10 minutes).</p>
          <div class="grid md:grid-cols-2 gap-4">
            <div>
              <h5 class="font-semibold text-indigo-800 mb-2">Preparation Time:</h5>
              <ul class="text-sm text-indigo-700 space-y-1">
                <li>• Same day: 45 minutes preparation</li>
                <li>• Advance notice: Topic given days before</li>
              </ul>
            </div>
            <div>
              <h5 class="font-semibold text-indigo-800 mb-2">Sample Topics:</h5>
              <ul class="text-sm text-indigo-700 space-y-1">
                <li>• "Why would you make a good specialist?"</li>
                <li>• "How are current changes affecting the specialty?"</li>
                <li>• "What interests you outside of medicine?"</li>
              </ul>
            </div>
          </div>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-yellow-600">👥</span> Group Discussions
        </h3>
        <div class="bg-yellow-50 p-4 rounded-lg mb-6 border-l-4 border-yellow-400">
          <p class="text-yellow-800 mb-3">Candidates are placed in groups of three or four and given a topic to debate for usually 20 minutes.</p>
          <div class="grid md:grid-cols-2 gap-4">
            <div>
              <h5 class="font-semibold text-yellow-800 mb-2">Types:</h5>
              <ul class="text-sm text-yellow-700 space-y-1">
                <li>• Simple discussion around hot topics</li>
                <li>• Organizing specific events</li>
                <li>• Discussing documents or papers</li>
              </ul>
            </div>
            <div>
              <h5 class="font-semibold text-yellow-800 mb-2">Role-Based:</h5>
              <ul class="text-sm text-yellow-700 space-y-1">
                <li>• Each candidate plays different role</li>
                <li>• Argue case for common problem</li>
                <li>• Roles: SHO, nurse, manager, patient rep</li>
              </ul>
            </div>
          </div>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-gray-600">🩺</span> OSCE (Objective Structured Clinical Examination) Station
        </h3>
        <div class="bg-gray-50 p-4 rounded-lg mb-6 border-l-4 border-gray-400">
          <p class="text-gray-800 mb-3">This assesses your clinical skills through practical scenarios.</p>
          <div class="space-y-3">
            <p class="text-gray-700">You may be asked to:</p>
            <ul class="text-sm text-gray-700 ml-4 space-y-1">
              <li>• Examine a patient</li>
              <li>• Take a history</li>
              <li>• Demonstrate a procedure</li>
              <li>• Discuss clinical management</li>
            </ul>
            <div class="bg-gray-100 p-3 rounded-lg mt-3">
              <h5 class="font-semibold text-gray-800 mb-2">Recent Examples:</h5>
              <ul class="text-sm text-gray-700 space-y-1">
                <li><strong>Orthopaedics:</strong> Naming specific bones</li>
                <li><strong>Surgery:</strong> Suturing orange peel or tomato skin</li>
                <li><strong>Anaesthetics:</strong> Intubating a dummy</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🎯</span>
            Key Takeaways for NHS Interview Success
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Different specialties and deaneries may structure stations differently. The actual structure matters little - you need to prepare for every type of question regardless of the order in which they are asked.</p>
          <p class="text-indigo-100 font-semibold">Focus on being competent in your clinical skills, thinking laterally, and demonstrating strong communication abilities. Each station tests different competencies essential for your future role.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">📚</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the NHS Interview Team</p>
              <p class="text-slate-700 font-medium">The NHS Interview Team provides comprehensive guidance on interview structures, stations, and preparation strategies based on official NHS recruitment frameworks and candidate success patterns across all specialties.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 2,
      slug: 'medical-interviews-transformed',
      category: 'Interview Insights',
      title: 'How Medical Interviews Have Transformed: From Conversation to Structured Assessment',
      excerpt: 'Medical interviews have evolved dramatically. What was once a conversational exchange is now a highly structured process requiring deliberate preparation and self-awareness.',
      author: 'Medical Interview Team',
      date: 'Dec 25, 2024',
      readTime: '7 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl border-l-4 border-red-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">🔄</span>
            </div>
            <h3 class="text-lg font-bold text-red-800">The Evolution of Medical Interviews</h3>
          </div>
          <p class="text-red-700 italic">What was once a conversational exchange has transformed into a highly structured assessment process, often involving multiple stations, time pressure, and detailed scoring systems.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Strong Performance Requires Deliberate Preparation
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">This shift has raised the bar for candidates, not because expectations are unrealistic, but because preparation has become more important than ever. Strong performance is no longer a matter of chance; it is the result of deliberate and informed preparation.</p>

        <div class="bg-amber-50 border-l-4 border-amber-400 p-4 mb-8 rounded-r-lg">
          <p class="text-amber-800 font-medium">🎯 <strong>Key Insight:</strong> Doing well in interviews depends on more than intelligence or clinical knowledge. It requires insight, self-awareness, and the ability to communicate effectively under pressure.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Start With Self-Awareness: The Foundation of Authenticity
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">An interview asks a simple but demanding question: <strong>Why should we choose you?</strong> Answering this convincingly is only possible if you genuinely understand yourself.</p>

        <div class="bg-slate-50 p-4 rounded-lg mb-6">
          <div class="flex items-start gap-3 mb-3">
            <span class="text-slate-500 mt-1">💭</span>
            <p class="text-slate-700">Many candidates can describe what makes a good doctor, yet struggle to explain how they embody those qualities.</p>
          </div>
          <div class="flex items-start gap-3">
            <span class="text-slate-500 mt-1">🎭</span>
            <p class="text-slate-700">Without reflection, confidence quickly collapses when interviewers ask for real examples.</p>
          </div>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-green-600">🎭</span> The Myth of "Right" Answers
        </h3>
        <p class="text-slate-700 leading-relaxed mb-4">There is a common belief that interviews reward the "right" answers — carefully worded responses that can be memorised and repeated. In reality, interviewers are listening for <strong>authenticity</strong>.</p>

        <div class="bg-green-50 p-4 rounded-lg mb-6 border-l-4 border-green-400">
          <p class="text-green-800 font-medium mb-3">They want to understand:</p>
          <ul class="space-y-2 text-green-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">🎯</span>
              <span>Your motivations and experiences</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📈</span>
              <span>How you have grown through them</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">📖</span>
              <span>Your personal narrative that no template can replace</span>
            </li>
          </ul>
        </div>

        <p class="text-slate-700 leading-relaxed mb-6"><strong>Before preparing answers, you must therefore prepare yourself.</strong> This means examining your experiences honestly and identifying what you have learned from them.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          Communication Matters More Than You Think
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Contrary to expectation, interviews rarely focus on testing how much you know. Instead, they assess <strong>how you think, how you explain, and how you engage</strong>.</p>

        <div class="bg-purple-50 p-4 rounded-lg mb-6">
          <p class="text-purple-800 font-medium mb-3">Your ability to communicate clearly, thoughtfully, and professionally is often far more influential than recalling facts.</p>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-teal-600">🗣️</span> Structured Approaches for Effective Communication
        </h3>
        <p class="text-slate-700 leading-relaxed mb-4">Effective communication is not improvised. Just as clinicians rely on structured approaches when assessing patients, interview candidates benefit from clear frameworks that help organise their thoughts.</p>

        <div class="bg-teal-50 p-4 rounded-lg mb-6 border-l-4 border-teal-400">
          <p class="text-teal-800 font-medium mb-3">These frameworks allow you to:</p>
          <ul class="space-y-2 text-teal-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">⏰</span>
              <span>Answer complex questions calmly, even under strict time constraints</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">🎯</span>
              <span>Deliver answers that are confident, coherent, and adaptable</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">📝</span>
              <span>Sound authentic rather than scripted</span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          Preparation Is a Skill, Not a Talent
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">Strong interview performances may appear effortless, but they are rarely unprepared. Like public speakers or performers, successful candidates refine both what they say and how they say it.</p>

        <div class="bg-indigo-50 p-4 rounded-lg mb-6">
          <p class="text-indigo-800 font-medium mb-3">Practice transforms good ideas into clear, persuasive communication.</p>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-pink-600">💪</span> Building Confidence Through Practice
        </h3>
        <p class="text-slate-700 leading-relaxed mb-4">Not everyone begins with natural confidence, and that is not a disadvantage. With focused practice, anyone can improve their delivery, timing, and presence.</p>

        <div class="bg-pink-50 p-4 rounded-lg mb-6 border-l-4 border-pink-400">
          <ul class="space-y-2 text-pink-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🎤</span>
              <span>Whether your interview is days away or months in the future, focused preparation is key</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🎯</span>
              <span>Interview questions often differ in wording but not in purpose</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🎪</span>
              <span>Mastering themes rather than memorising answers gives you flexibility and control</span>
            </li>
          </ul>
        </div>

        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🌟</span>
            The Transformative Power of Interview Preparation
          </h3>
          <p class="text-indigo-100 leading-relaxed mb-4">Approached in this way, interviews become less about fear and more about clarity: an opportunity to present who you are, what you value, and what you are capable of becoming.</p>
          <p class="text-indigo-100 font-semibold">Strong performance is no longer a matter of chance; it is the result of deliberate and informed preparation that transforms good candidates into exceptional ones.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">📚</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">About the Author</p>
              <p class="text-slate-700 font-medium">Dr. James Mitchell is a Consultant Physician and NHS interview panel chair with 20+ years of experience mentoring medical trainees. He specialises in communication skills and professional development.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 2,
      slug: 'managing-the-interview',
      category: 'Interview Skills',
      title: 'Managing the Interview: Essential Techniques for NHS Success',
      excerpt: 'Master the art of interview management with proven techniques that will help you present yourself in the best possible light to NHS interview panels.',
      author: 'Medical Interview Team',
      date: 'Dec 22, 2024',
      readTime: '5 min read',
      tag: 'All Grades',
      content: `
        <div class="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-l-4 border-blue-500">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span class="text-white text-sm font-bold">💡</span>
            </div>
            <h3 class="text-lg font-bold text-blue-800">Important Note</h3>
          </div>
          <p class="text-slate-700 italic">NHS interviews assess not just your clinical competence, but also your communication skills, professionalism, and organisational fit. Good preparation significantly increases your chances of success.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
          Why Interview Management Matters
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">However much preparation you do beforehand, going into an interview is usually slightly nerve-wracking. Do not make the mistake of thinking that, after entering the room, the situation is out of your hands. There are still a number of techniques you can use to make sure the panel sees you in the best possible light.</p>

        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-r-lg">
          <p class="text-yellow-800 font-medium">🎯 <strong>Success Key:</strong> Interview management is about controlling what you can control. Preparation, professional presentation, and positive engagement will help you demonstrate why you're the right candidate for the role.</p>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
          Interview Preparation: Foundation of Success
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">It might appear obvious but it is important that you prepare for the day. Thorough preparation builds confidence and ensures you can focus on performing rather than remembering details.</p>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-green-600">📋</span> Know Your Application Inside Out
        </h3>
        <div class="bg-slate-50 p-4 rounded-lg mb-6">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">✓</span>
              <span>Make sure you are familiar with the job description and person specification</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-green-500 mt-1">✓</span>
              <span>Remind yourself of what you wrote in your application on how you met those requirements</span>
            </li>
          </ul>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-blue-600">🏢</span> Research the Organisation
        </h3>
        <p class="text-slate-700 leading-relaxed mb-4">Learn about the organisation itself – what makes it different from others and whether those differences will have an impact on your work. Understanding the NHS trust's values, challenges, and priorities shows genuine interest.</p>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-purple-600">🗣️</span> Prepare Your Responses
        </h3>
        <div class="bg-purple-50 p-4 rounded-lg mb-6">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">💭</span>
              <span>Think about what type of questions will be asked about yourself and write down some thoughts on how you might answer them</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-purple-500 mt-1">❓</span>
              <span>Prepare some questions to ask the panel. Be careful not to ask for information that has already been provided or discussed during the interview</span>
            </li>
          </ul>
        </div>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
          First Impressions: Setting the Tone
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">There are a couple of simple steps to take to create a good first impression. Remember, the interview begins the moment you enter the building.</p>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-red-600">⏰</span> Time Management
        </h3>
        <div class="bg-red-50 p-4 rounded-lg mb-6 border-l-4 border-red-400">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">🕐</span>
              <span>Arrive in plenty of time and allow for contingencies</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">🗺️</span>
              <span>Remember some organisations can be quite difficult to navigate round</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">🚗</span>
              <span>Have a 'Plan B' for how you will get to the interview</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-red-500 mt-1">📞</span>
              <span>If you do get delayed, make sure you notify the appropriate person within the organisation as soon as possible</span>
            </li>
          </ul>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-indigo-600">👔</span> Professional Appearance
        </h3>
        <p class="text-slate-700 leading-relaxed mb-6">Dress appropriately making sure that you send out the right signals about your confidence, self discipline and judgement. It might be useful to check the dress code of the organisation by looking at pictures of staff on the organisation's website or asking around.</p>

        <h2 class="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <span class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
          During the Interview: Performance Techniques
        </h2>
        <p class="text-slate-700 leading-relaxed mb-6">The moment you've been preparing for. Here are the key techniques to maximise your performance during the interview itself.</p>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-teal-600">👁️</span> Body Language and Engagement
        </h3>
        <div class="bg-teal-50 p-4 rounded-lg mb-6">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">👀</span>
              <span>Make eye contact with all members of the interview panel. Do not just focus on the person who has asked the question you are answering. It is important to engage with all</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">😊</span>
              <span>Smile! It suggests confidence</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-teal-500 mt-1">💪</span>
              <span>Be aware of your body language. You want to convey that you're enthusiastic, positive and energetic</span>
            </li>
          </ul>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-pink-600">💬</span> Communication Skills
        </h3>
        <div class="bg-pink-50 p-4 rounded-lg mb-6">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">🎯</span>
              <span>Be clear and concise in your responses</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">📝</span>
              <span>Structure your answers with 3 or 4 main points of examples from your own experience</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-pink-500 mt-1">📄</span>
              <span>Do not assume that the panel know the detail of what is in your application form or CV</span>
            </li>
          </ul>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-emerald-600">🏆</span> Showcase Your Achievements
        </h3>
        <div class="bg-emerald-50 p-4 rounded-lg mb-6 border-l-4 border-emerald-400">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">💼</span>
              <span>Sell yourself - give real examples of 'how' you have achieved a positive outcome, specify what these were and the benefits</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">🎖️</span>
              <span>Be clear about what your personal contribution was</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-emerald-500 mt-1">👤</span>
              <span>Remember to use 'I' rather than 'we' when describing your individual achievements</span>
            </li>
          </ul>
        </div>

        <h3 class="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span class="text-cyan-600">🤝</span> Demonstrate Organisational Fit
        </h3>
        <div class="bg-cyan-50 p-4 rounded-lg mb-6">
          <ul class="space-y-2 text-slate-700">
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">🎯</span>
              <span>Make sure you understand the organisation's mission and can talk about why it is important to you</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">❓</span>
              <span>Remember to ask questions as it helps demonstrate that you are keen and want the job</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-cyan-500 mt-1">😄</span>
              <span>Be positive at all times</span>
            </li>
          </ul>
        </div>

        <div class="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-2xl mb-8">
          <h3 class="text-xl font-bold mb-4 flex items-center gap-3">
            <span class="text-2xl">🎯</span>
            Key Takeaways for NHS Interview Success
          </h3>
          <p class="text-purple-100 leading-relaxed mb-4">Mastering interview management is about controlling what you can control. Preparation, professional presentation, and positive engagement will help you demonstrate why you're the right candidate for the role.</p>
          <p class="text-purple-100 font-semibold">Remember: NHS interviews assess not just your clinical competence, but your communication skills, professionalism, and fit with the organisation's values. Practice these techniques, stay confident, and show your genuine enthusiasm for healthcare.</p>
        </div>

        <div class="border-t border-slate-200 pt-8 mt-12">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span class="text-slate-600 font-bold">📚</span>
            </div>
            <div>
              <p class="text-sm font-bold text-slate-600 uppercase tracking-wider">Source</p>
              <a href="https://www.jobs.nhs.uk/candidate/search/advice/managing-the-interview" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 font-medium">NHS Jobs - Managing the Interview</a>
            </div>
          </div>
          <p class="text-slate-500 italic text-sm">The NHS Interview Team provides guidance based on official NHS recruitment best practices and candidate success patterns.</p>
        </div>
      `
    }
  ];

  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#fdfdfe] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black text-slate-900 mb-4">Blog Post Not Found</h1>
          <button
            onClick={() => navigate('/blog')}
            className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-blue-700 transition-all"
          >
            Return to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfdfe] relative overflow-hidden">
      {/* Background patterns */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none opacity-40">
        <div className="absolute top-[-10%] left-[20%] w-[800px] h-[800px] bg-blue-50 rounded-full blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-6 py-20 relative z-10 max-w-4xl">
        {/* Back button */}
        <button
          onClick={() => window.location.href = '/blog'}
          className="group flex items-center gap-3 bg-white border-2 border-slate-100 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:border-blue-600 hover:text-blue-600 transition-all shadow-sm active:scale-95 mb-8"
        >
          <svg className="group-hover:translate-x-[-4px] transition-transform" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Back to Blog
        </button>

        {/* Article header */}
        <header className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="bg-blue-600 text-white px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest">{post.category}</span>
            <span className="bg-slate-50 text-slate-400 text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-tighter">{post.tag}</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-6">
            {post.title}
          </h1>

          <p className="text-slate-500 font-medium leading-relaxed mb-8 text-lg">
            {post.excerpt}
          </p>

          <div className="flex items-center justify-between border-t border-slate-50 pt-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center font-black text-slate-400 text-sm">
                {post.author.charAt(0)}
              </div>
              <div>
                <p className="text-sm font-black text-slate-800 uppercase tracking-tight">{post.author}</p>
                <p className="text-[11px] font-bold text-slate-400">{post.date}</p>
              </div>
            </div>
            <span className="text-[11px] font-black text-blue-400 uppercase tracking-widest">{post.readTime}</span>
          </div>
        </header>

        {/* Article content */}
        <article className="prose prose-lg max-w-none">
          <div
            className="text-slate-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </article>

        {/* Related Articles */}
        <RelatedArticles currentPost={post} allPosts={blogPosts} />

        {/* Related content / CTA */}
        <div className="mt-16 p-8 bg-slate-900 rounded-[3rem] text-white flex flex-col items-center text-center relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/20 blur-[60px] pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-emerald-600/10 blur-[60px] pointer-events-none"></div>

          <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-[0.4em] mb-4">Ready to Apply?</h4>
          <h3 className="text-2xl font-black mb-4 tracking-tight">Practice with CAMP Framework</h3>
          <p className="text-slate-400 max-w-md mb-6 font-medium">Use our AI-powered interview simulator to practice CAMP-structured answers and get personalized feedback.</p>

          <button
            onClick={() => window.location.href = '/'}
            className="bg-blue-600 px-8 py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-blue-700 transition-all active:scale-95 shadow-xl shadow-blue-900/40"
          >
            Start Practicing Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;